import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { useSpeechSettings } from '@/api/speechSettings';
import { useServerHealth } from '@/lib/serverHealth';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Bell, Check, ChevronDown, ChevronsDownUp, CreditCard, Hourglass, Info, Link2, ListFilter, Loader2, Mic, Nfc, Pause, Play, Printer, Settings2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Popover } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Tooltip } from '@/components/ui/tooltip';
import { useT } from '@/contexts/I18nContext';
import { loadConversationAudio } from '@/lib/conversationAudio';
import { getRate, useRate } from '@/lib/playbackRate';
import RateButton from '@/components/RateButton';
import ConversationsOverview from './ConversationsOverview';
import type { ConversationsOverview as OverviewData } from '@/api/conversations';
import { useExactRecording } from '@/lib/exactPlayer';
import { NoDataForPeriod, isoDay } from '@/components/common/NoDataForPeriod';
import { ChipStepPicker, SearchChipInput, type PickerKind, type PickerValue } from '@/components/common/SearchChipInput';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import {
  flagConversation,
  getConversations,
  type Conversation,
  type ConversationRawLlmResponse,
  type ConversationProblem,
  type ProblemSeverity,
  type DialogueLine,
  type TranscriptLine,
  type UpsellAttempt,
} from '@/api/conversations';

function NoMicValueProp() {
  const { t } = useT();
  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        <div className="flex items-start gap-3">
          <Mic className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
          <h3 className="text-base font-semibold">{t('analytics.speech.noMic.title')}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t('analytics.speech.noMic.summary')}</p>
      </CardContent>
    </Card>
  );
}


/** Stacked native players for a conversation's per-segment recordings. */
// Pin each clip's audio src to its first URL — the S3 presigned signature is
// regenerated on every refetch, so a revalidation mid-playback would otherwise
// swap the <audio src> and reload (truncate) the clip.
function usePinnedSrc() {
  const cache = useRef(new Map<string, string>());
  const pin = (key: string, url: string) => {
    if (!cache.current.has(key)) cache.current.set(key, url);
    return cache.current.get(key)!;
  };
  // Подпись у ссылки не вечная: закреплённая ссылка со временем протухает, и
  // недокачанная часть записи отвечает 403. Тогда берём свежую ссылку из данных —
  // если она уже другая. true — ссылка сменилась.
  const repin = (key: string, url: string) => {
    if (cache.current.get(key) === url) return false;
    cache.current.set(key, url);
    return true;
  };
  return { pin, repin };
}

// Играет только один плеер на странице: старт одного ставит на паузу остальные
// (владелец, 14.09). Шина на модуль, а не контекст: плееры живут в разных
// ветках дерева, и провайдер ради одного события был бы лишним.
const playerBus = new EventTarget();

/** One conversation = many ~30 s clips. Glued into a SINGLE player (Alexey
 *  09.09: «склей все аудио разговора в один компонент… с еле заметными
 *  засечками по 30 с уже для нас») — the stack of native <audio> controls made
 *  a conversation read as N unrelated recordings and forced N clicks to listen
 *  through it.
 *
 *  Playback chains the clips through one <audio>: on `ended` we advance to the
 *  next source and keep playing, so the seam is a src swap rather than a stop.
 *  The scrubber is a GLOBAL timeline over the summed durations, and the clip
 *  boundaries are drawn as faint ticks — deliberately low-contrast, they are a
 *  diagnostic for us (where the chunker cut), not information for the branch. */
type Clip = {
  sequence_number: number;
  audio_url: string;
  recorded_at?: string | null;
  duration?: number | null;
  /** Запасная длительность — только если браузер свою не знает (Safari и ogg).
   *  В отличие от duration, НЕ перебивает длительность, прочитанную у файла. */
  fallbackDuration?: number | null;
};

/**
 * Continuous playback over a conversation's clips.
 *
 * The clips are separate ~30 s files but one conversation, so everything here
 * works in GLOBAL seconds (from the first clip's start) and the hook maps that
 * onto (clip index, offset within clip). Shared by the compact player bar and
 * the dialogue board — the board needs `elapsed` to highlight the current line
 * and `seekTo` to jump when a timecode is clicked.
 */
function useClipPlayer(clips: Clip[], active = true) {
  const { pin, repin } = usePinnedSrc();
  // По ОДНОМУ элементу на клип, а не один на всех.
  //
  // Раньше был один <audio>, и при переходе между клипами ему переставляли src и
  // звали load(): браузер выбрасывал уже скачанное и шёл в сеть заново. Пока сеть
  // отвечает быстро, это незаметно; стоит ей задуматься — плеер «зависает» после
  // перетаскивания или паузы, что владелец и увидел. Отдельные элементы держат
  // свои буферы, поэтому возврат к уже слышанному клипу мгновенный, а сеть
  // трогается один раз на клип.
  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
  const [durations, setDurations] = useState<number[]>([]);
  const [idx, setIdxState] = useState(0);
  // Какой клип СЕЙЧАС текущий — ещё и в ref, а не только в состоянии.
  //
  // Обработчики событий <audio> замыкают idx того рендера, в котором созданы. При
  // переходе по репликам в ДРУГОЙ клип старый элемент успевает прислать
  // timeupdate и pause до перерисовки: по устаревшему idx он считался текущим,
  // сбрасывал позицию на своё время и ставил «пауза», пока новый клип уже играл.
  // Дальше дорожка уезжала за конец разговора, срабатывала остановка у конца, и
  // кнопка переключала не тот элемент — плеер «зависал» (владелец, 14.09).
  const idxRef = useRef(0);
  const setIdx = (i: number) => {
    idxRef.current = i;
    setIdxState(i);
  };
  const [offset, setOffset] = useState(0);
  const offsetRef = useRef(0);
  offsetRef.current = offset;
  const [playing, setPlaying] = useState(false);
  // Звук просили, а данных ещё нет (грузится или завис запрос) — кнопка крутится,
  // а не молчит (владелец 14.09 и 16.09: «нужен лоадер, а не молчаливое зависание»).
  const [waiting, setWaiting] = useState(false);
  // Хотим ли мы, чтобы звук шёл, — независимо от того, что сейчас делает
  // элемент. Нужно, чтобы после замены протухшей ссылки продолжить с того же
  // места и в том же состоянии.
  const wantPlay = useRef(false);
  // Отложенный переход: элемент пересоздан со свежей ссылкой и ещё не прочитал
  // метаданные — куда встать и играть ли, когда прочитает.
  const pending = useRef<{ i: number; within: number; resume: boolean } | null>(null);
  const [, bump] = useState(0);
  const selfId = useRef({});
  useEffect(() => {
    const onOtherPlay = (e: Event) => {
      if ((e as CustomEvent).detail === selfId.current) return;
      wantPlay.current = false;
      audioRefs.current.forEach((el) => {
        if (el && !el.paused) el.pause();
      });
    };
    playerBus.addEventListener('play', onOtherPlay);
    return () => playerBus.removeEventListener('play', onOtherPlay);
  }, []);

  const ordered = [...clips].sort((a, b) => a.sequence_number - b.sequence_number);
  // blob: — файл уже в памяти (lib/conversationAudio.ts): закреплять нечего, протухать нечему.
  const srcs = ordered.map((c) => (c.audio_url.startsWith('blob:') ? c.audio_url : pin(String(c.sequence_number), c.audio_url)));
  // Ссылка текущего клипа сменилась (файл разговора пришёл в память): элемент
  // пересоздаётся — продолжить с того же места и в том же состоянии. Читаем старый
  // элемент здесь, пока он ещё смонтирован.
  const prevSrcs = useRef(srcs);
  if (prevSrcs.current[idxRef.current] !== undefined && prevSrcs.current[idxRef.current] !== srcs[idxRef.current]) {
    const i = idxRef.current;
    const el = audioRefs.current[i];
    pending.current = { i, within: el && el.readyState >= 1 ? el.currentTime : offsetRef.current, resume: wantPlay.current };
  }
  prevSrcs.current = srcs;
  const orderedRef = useRef(ordered);
  orderedRef.current = ordered;

  // Ряд свернули — звук на паузу (Эдем, 14.09). Содержимое закрытого <details>
  // остаётся смонтированным, поэтому без этого разговор продолжал играть
  // невидимо. Именно пауза, не стоп: позиция сохраняется, и после раскрытия
  // плей продолжает с того же места.
  useEffect(() => {
    if (active) return;
    wantPlay.current = false;
    pending.current = null;
    audioRefs.current.forEach((el) => {
      if (el && !el.paused) el.pause();
    });
    setPlaying(false);
  }, [active]);

  // СТОРОЖ ЗАВИСАНИЯ. Владелец 14.09, Chrome: «кнопка меняет плей на паузу, но
  // воспроизведение не идёт, и не оживает», а в Network висит pending. Это
  // заглохший запрос звука: play() ждёт данных вечно, ошибки нет, и ни один
  // обработчик не срабатывает. Поэтому смотрим на сам звук: состояние «играет»,
  // а позиция не двигается 4 секунды — перезапрашиваем файл (свежей ссылкой,
  // если она уже есть) и продолжаем с того же места. Не больше трёх попыток
  // подряд, чтобы мёртвый файл не долбил сеть.
  const stall = useRef({ t: -1, since: 0, tries: 0 });
  useEffect(() => {
    if (!playing) {
      stall.current = { t: -1, since: 0, tries: 0 };
      return;
    }
    const id = window.setInterval(() => {
      const i = idxRef.current;
      const el = audioRefs.current[i];
      if (!el) return;
      const now = performance.now();
      if (el.currentTime !== stall.current.t || stall.current.since === 0) {
        stall.current.t = el.currentTime;
        stall.current.since = now;
        return;
      }
      if (now - stall.current.since < 4000 || stall.current.tries >= 3) return;
      stall.current.tries += 1;
      stall.current.since = now;
      // Не прочитавший метаданных элемент стоит на нуле, а ждали мы другое место
      // (клик по реплике на 42 с) — перезапрос должен вернуть туда, а не в начало.
      const within = el.readyState >= 1 ? el.currentTime : offsetRef.current;
      pending.current = { i, within, resume: true };
      const clip = orderedRef.current[i];
      if (clip && repin(String(clip.sequence_number), clip.audio_url)) {
        bump((n) => n + 1);
      } else {
        el.load();
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [playing]);

  // Длительности берём у ТЕХ ЖЕ элементов, что и играют: раньше для замера
  // создавались отдельные Audio, то есть каждый клип запрашивался дважды — один
  // раз ради длительности, второй ради звука. Нарезка целится в 30 секунд, но
  // последний кусок короче, поэтому считать ширину фиксированной нельзя: замер
  // нужен, а вот второй запрос — нет.
  function noteDuration(i: number, value: number) {
    setDurations((prev) => {
      if (!Number.isFinite(value) || prev[i] === value) return prev;
      const next = [...prev];
      while (next.length < srcs.length) next.push(0);
      next[i] = value;
      return next;
    });
  }

  // ДОРОЖКА — ЭТО РЕАЛЬНОЕ ВРЕМЯ ЗАПИСИ, а не склейка клипов встык.
  //
  // Между клипами бывает дыра: окно захвата закрывалось, ffmpeg перезапускался.
  // Пока плеер клеил клипы подряд, а бэкенд считал время по длительностям, эти
  // две шкалы расходились, и подсветка реплик отставала от звука. Теперь обе
  // шкалы строятся от recorded_at, то есть от одного и того же физического
  // времени, и сходятся одна в одну. Длительность берём серверную (посчитана по
  // самому файлу) — она же лежит в основе времени реплик.
  const lens = ordered.map((c, i) => c.duration ?? (durations[i] || c.fallbackDuration || 0));
  const base = ordered[0]?.recorded_at ? Date.parse(ordered[0].recorded_at) : null;
  const exact = base !== null && ordered.every((c) => c.recorded_at);
  const starts = ordered.map((c, i) => {
    if (exact && c.recorded_at) return (Date.parse(c.recorded_at) - (base as number)) / 1000;
    // Записи до 10.09 времени записи не знают — там остаётся склейка встык.
    return lens.slice(0, i).reduce((a, b) => a + b, 0);
  });
  const total = starts.length ? starts[starts.length - 1] + (lens[lens.length - 1] || 0) : 0;
  const elapsed = (starts[idx] ?? 0) + offset;
  const startsRef = useRef(starts);
  startsRef.current = starts;

  // «Играть один сегмент» (S7, владелец 15.09): «нажатие на этот сегмент
  // проигрывает вместе с ним и следующий». Остановка жила в компоненте и
  // срабатывала по timeupdate, а браузер шлёт его раз в ~250 мс плюс перерисовка
  // React: отрезок 0.4 с переигрывал до 0.35 с — ровно следующее слово. Теперь
  // конец стоит здесь и сверяется с самим элементом на каждом кадре.
  const stopAt = useRef<number | null>(null);
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      const stop = stopAt.current;
      const i = idxRef.current;
      const el = audioRefs.current[i];
      if (stop != null && el && (startsRef.current[i] ?? 0) + el.currentTime >= stop) {
        stopAt.current = null;
        wantPlay.current = false;
        el.pause();
        setOffset(el.currentTime);
        setPlaying(false);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  /** Поставить клип `i` на `within` секунд и, если просили, играть.
   *
   *  currentTime у элемента, который ещё не прочитал метаданные, браузер молча
   *  игнорирует, поэтому в этом случае ждём loadedmetadata. Остальные элементы
   *  ставим на паузу: иначе после перехода играют два клипа сразу. */
  function playAt(i: number, within: number, resume: boolean) {
    const el = audioRefs.current[i];
    if (!el) return;
    el.playbackRate = getRate();
    wantPlay.current = resume || wantPlay.current;
    audioRefs.current.forEach((other, k) => {
      if (other && k !== i && !other.paused) other.pause();
    });
    if (resume && el.readyState < 3) setWaiting(true);
    const apply = () => {
      try {
        el.currentTime = within;
      } catch {
        // Метаданных ещё нет — поставим позицию, когда придут.
      }
      if (resume) {
        // Отклонённый play() (прерван следующим переходом, не загружен) не
        // присылает pause — сверяем состояние с самим элементом.
        el.play().catch(() => {
          if (idxRef.current === i) setPlaying(!el.paused);
        });
      }
    };
    if (el.readyState >= 1) {
      apply();
    } else if (resume) {
      // ИГРАТЬ — СРАЗУ, прямо в нажатии, а не из loadedmetadata. Safari на
      // iPhone разрешает звук только синхронно в обработчике нажатия: play(),
      // отложенный до прихода метаданных, он молча отклоняет (NotAllowedError).
      // Раньше здесь ещё и звали load() — он сбрасывал элемент, и отложенный
      // play() на iPhone не срабатывал никогда: «время появилось, но плей не
      // включает звук» (Эдем, 14.09). play() сам запускает загрузку в любом
      // браузере; позицию ставим, когда метаданные придут.
      if (within > 0) {
        el.addEventListener('loadedmetadata', () => {
          try { el.currentTime = within; } catch { /* позиция не критична */ }
        }, { once: true });
      }
      el.play().catch(() => {
        if (idxRef.current === i) setPlaying(!el.paused);
      });
    } else {
      el.addEventListener('loadedmetadata', apply, { once: true });
      // У свёрнутого ряда предзагрузка выключена: смена атрибута сама загрузку
      // не запускает — просим явно, иначе метаданные не придут никогда.
      if (el.preload === 'none' || el.readyState === 0) {
        el.preload = 'metadata';
        if (el.networkState !== el.NETWORK_LOADING) el.load();
      }
    }
  }

  /** stop — где остановиться (глобальные секунды); без него играет дальше. */
  function seekTo(globalSec: number, autoplay = false, hold = false, stop: number | null = null) {
    stopAt.current = stop;
    for (let i = 0; i < starts.length; i++) {
      const end = starts[i] + (lens[i] || 0);
      // Точка внутри клипа — играем с неё. Точка в дыре между клипами — звука
      // там не существует, поэтому переходим к началу следующего клипа: это
      // честнее, чем делать вид, что тишину можно проиграть.
      if (globalSec < end || i === starts.length - 1) {
        const within = Math.max(0, Math.min(globalSec - starts[i], lens[i] || 0));
        setIdx(i);
        setOffset(within);
        // Во время перетаскивания только двигаем позицию: play() на каждый
        // пиксель обрывался следующим же вызовом, элемент оставался на паузе,
        // а состояние «играет» — нет, и кнопка переставала слушаться.
        if (hold) {
          const el = audioRefs.current[i];
          if (el && el.readyState >= 1) {
            try { el.currentTime = within; } catch { /* до метаданных — игнорируется */ }
          }
          return;
        }
        playAt(i, within, autoplay || playing);
        if (autoplay) setPlaying(true);
        return;
      }
    }
  }

  // Скорость меняют кнопкой на ходу — переставляем у всех элементов цепочки.
  const rate = useRate();
  useEffect(() => {
    audioRefs.current.forEach((el) => { if (el) el.playbackRate = rate; });
  }, [rate]);

  /** Звук не идёт и не просили: элемент можно пересоздать незаметно. */
  const idle = () => !wantPlay.current && !playing && pending.current == null;
  /** Просили играть, а данных для хода нет (запрос к хранилищу завис): лучше
   *  пересоздать элемент на файле в памяти, чем ждать. */
  const starved = () => {
    const el = audioRefs.current[idxRef.current];
    return !el || el.readyState < 3;
  };

  function toggle() {
    stopAt.current = null;
    const cur = idxRef.current;
    const el = audioRefs.current[cur];
    if (!el) return;
    if (playing) {
      wantPlay.current = false;
      el.pause();
      setPlaying(false);
      return;
    }
    playAt(cur, el.readyState >= 1 ? el.currentTime : offset, true);
    setPlaying(true);
  }

  // Швы клипов в процентах от дорожки — по реальному времени записи.
  const ticks: number[] = [];
  for (let i = 1; i < starts.length; i++) {
    if (total > 0) ticks.push((starts[i] / total) * 100);
  }

  const element = (
    <>
      {srcs.map((src, i) => (
        <audio
          key={src}
          ref={(node) => {
            audioRefs.current[i] = node;
          }}
          src={src}
          // Метаданные — только у раскрытого разговора. Свёрнутые ряды тоже
          // монтируют плеер, и двадцать элементов с preload=metadata держали
          // открытые запросы к хранилищу: новый запрос при переходе по
          // сегментам вставал в очередь за ними (pending) — и плеер «зависал».
          preload={active ? 'metadata' : 'none'}
          className="hidden"
          onLoadedMetadata={(e) => {
            noteDuration(i, e.currentTarget.duration);
            const p = pending.current;
            if (p && p.i === i) {
              pending.current = null;
              playAt(p.i, p.within, p.resume);
              return;
            }
          }}
          // Протухла ссылка (или сеть отдала ошибку): элемент мёртв до
          // перезагрузки страницы — ровно то «зависание намертво», что видел
          // владелец 14.09. Если в данных уже свежая ссылка — пересоздаём элемент
          // с ней и продолжаем с того же места.
          onError={(e) => {
            const clip = ordered[i];
            const isCur = i === idxRef.current;
            if (clip && repin(String(clip.sequence_number), clip.audio_url)) {
              if (isCur) {
                pending.current = { i, within: e.currentTarget.currentTime || 0, resume: wantPlay.current };
              }
              bump((n) => n + 1);
              return;
            }
            if (isCur) setPlaying(false);
          }}
          // Safari у ogg отдаёт в loadedmetadata Infinity и узнаёт настоящую
          // длительность позже, когда файл докачан, — ловим её здесь, и она
          // сменяет запасную.
          onDurationChange={(e) => noteDuration(i, e.currentTarget.duration)}
          onTimeUpdate={(e) => {
            if (i === idxRef.current) setOffset(e.currentTarget.currentTime);
          }}
          // Состояние «играет» — по самому элементу: браузер может отклонить
          // play() (прерван паузой, ещё не загружен), и кнопка иначе врёт.
          onPlay={() => {
            playerBus.dispatchEvent(new CustomEvent('play', { detail: selfId.current }));
            if (i === idxRef.current) setPlaying(true);
          }}
          onPause={(e) => { if (i === idxRef.current && !e.currentTarget.ended) { setPlaying(false); setWaiting(false); } }}
          onWaiting={() => { if (i === idxRef.current) setWaiting(true); }}
          onPlaying={() => { if (i === idxRef.current) setWaiting(false); }}
          onEnded={() => {
            if (i !== idxRef.current) return;
            if (i < srcs.length - 1) {
              const next = i + 1;
              setIdx(next);
              setOffset(0);
              playAt(next, 0, true);
            } else {
              wantPlay.current = false;
              setPlaying(false);
              setIdx(0);
              setOffset(0);
            }
          }}
        />
      ))}
    </>
  );

  return { ordered, total, elapsed, playing, waiting: waiting && (playing || wantPlay.current), toggle, seekTo, ticks, element, idle, starved };
}

/** Compact play bar: one row, used where the dialogue board is not shown. */
function AudioClips({ clips }: { clips: Clip[] }) {
  const { t } = useT();
  const { ordered, total, elapsed, playing, toggle, seekTo, ticks, element } = useClipPlayer(clips);
  if (!ordered.length) return <span className="text-muted-foreground">—</span>;
  const pct = total > 0 ? Math.min(100, (elapsed / total) * 100) : 0;

  return (
    <div className="flex w-64 max-w-full items-center gap-2">
      <button
        type="button"
        onClick={toggle}
        aria-label={t('conversations.recording.clip')}
        className="flex size-7 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground transition-colors hover:bg-muted disabled:opacity-40"
      >
        {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5 translate-x-px" />}
      </button>

      <div
        className="group relative h-6 min-w-0 flex-1 cursor-pointer"
        onClick={(e) => {
          if (total <= 0) return;
          const r = e.currentTarget.getBoundingClientRect();
          seekTo(((e.clientX - r.left) / r.width) * total);
        }}
      >
        <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary/70" style={{ width: `${pct}%` }} />
        </div>
        {/* Clip seams — faint on purpose: internal diagnostic, not branch-facing. */}
        {ticks.map((left, i) => (
          <div
            key={i}
            className="absolute top-1/2 h-2 w-px -translate-y-1/2 bg-foreground/15"
            style={{ left: `${left}%` }}
          />
        ))}
      </div>

      <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground">
        {fmtClock(elapsed)}/{fmtClock(total)}
      </span>
      {element}
    </div>
  );
}

/** Пометки звука одной строкой: «сменился голос · кончается вопросом».
 *
 * Те же пометки, что видит модель. Чисел (дБ, Гц, тембр, форманты) не показываем:
 * они ничего не определяют и только путают (Алексей 26.10 #2596).
 */
function SoundFacts({ line }: { line: TranscriptLine }) {
  const bits: string[] = [];
  // Причина разреза строки — ровно та, что видел судья (13.09: вместо ярлыков).
  if (line.cut) bits.push(line.cut);
  // Конец предложения — пометка той строки, что его заканчивает (с 14.09).
  if (line.ends) bits.push(line.ends === 'вопрос' ? 'кончается вопросом' : line.ends);
  if (line.rel) bits.push(line.rel);
  if (line.quiet && !line.gap) bits.push('плохо слышно');
  // Два голоса сошлись вплотную (встык или вставка); та же пометка, что у судьи.
  if (line.overlap) bits.push('смена вплотную');
  if (!bits.length) return null;
  // Переносится МЕЖДУ фактами, но не внутри факта. Раньше вся строка была
  // одним whitespace-nowrap: «12 дБ · 106 Гц · тембр -16 · форманты 680/1540 ·
  // голос A · пауза» не влезала в телефон и растягивала прокрутку всей вкладки
  // до 476 px при экране 358 (Эдем, 14.09: «панель разговоров можно двигать
  // вбок»). Причём даже из СВЁРНУТЫХ клипов: закрытый <details> в нынешних
  // браузерах всё равно раскладывает содержимое и учитывает его ширину.
  return (
    <span className="min-w-0 text-[11px] tabular-nums text-muted-foreground/70">
      {bits.map((b, i) => (
        <React.Fragment key={i}>
          {i > 0 && ' · '}
          <span className="whitespace-nowrap">{b}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

/** Расшифровка как есть, в нашем формате: реплика, её звук, событие оплаты. */
function TranscriptView({ lines, fallback, onSeek, active = -1, inside = true }: {
  lines: TranscriptLine[];
  fallback: string;
  onSeek?: (sec: number, dur?: number | null) => void;
  /** Строка, которая играет или сыграла последней (индекс после фильтра пустых). */
  active?: number;
  /** Позиция внутри этой строки; иначе — в тишине после неё. */
  inside?: boolean;
}) {
  const { t } = useT();
  // Пустые строки не показываем: это либо событие без текста, либо огрызок ASR.
  // Строка с временем и пустотой ничего не сообщает, а список рвёт.
  lines = lines.filter((l) => (l.text || '').trim() || l.tone || l.gap);
  if (!lines.length) {
    return (
      <p className="whitespace-pre-wrap break-words text-muted-foreground">
        {fallback || t('conversations.internal.empty')}
      </p>
    );
  }
  return (
    <ol className="space-y-0.5">
      {lines.map((l, i) => (
        <li key={i} className="relative flex flex-wrap items-baseline gap-x-2 rounded px-2 py-1 hover:bg-muted/40">
          {/* Та же отметка, что в восстановленном диалоге: полоса слева у
              играющей строки, точка под ней — когда звук уже между строками. */}
          <PlayMark on={i === active} inside={inside} />
          <button
            type="button"
            onClick={() => onSeek?.(l.t ?? 0, l.dur)}
            className="w-12 shrink-0 rounded bg-muted/60 text-center font-mono text-[11px] tabular-nums text-primary hover:bg-primary/15"
          >
            {fmtClock(l.t ?? 0, true)}
          </button>
          {l.tone ? (
            // Оплата оранжевым, но НЕ жирным: это отметка события в потоке
            // реплик, она не должна перекрикивать сам разговор.
            <span className="flex-1 text-[13px] italic text-amber-600/80 dark:text-amber-500/80">
              {t(l.tone === 'скан' ? 'conversations.transcript.scan'
                : l.tone === 'звон' ? 'conversations.transcript.chime'
                : l.tone === 'чек' ? 'conversations.transcript.print'
                : 'conversations.transcript.beep',
                 { hz: String(l.hz_tone ?? '') })}
            </span>
          ) : l.gap ? (
            // Чем занят промежуток: тишина, шум зала или неразобранная речь.
            // Последнее — почти всегда пропущенная реплика гостя.
            <span className={`min-w-[9rem] flex-[1_1_9rem] text-xs italic ${l.gap === 'речь' || l.gap === 'вставка' ? 'text-foreground/70' : 'text-muted-foreground/60'}`}>
              {t(l.gap === 'речь' ? (l.quiet ? 'conversations.transcript.unclearFar' : 'conversations.transcript.unclear')
                : l.gap === 'вставка' ? (l.hz2 ? 'conversations.transcript.insert' : 'conversations.transcript.murmur')
                : l.gap === 'шум' ? 'conversations.transcript.noise'
                : l.gap === 'стук' ? 'conversations.transcript.taps'
                : 'conversations.transcript.silence',
                { sec: (l.dur ?? 0).toFixed(1), hz: String(l.hz2 ?? '') })}
            </span>
          ) : (
            // Текст главнее чисел справа (S2, владелец 15.09): у текста нижний предел
            // ширины, и когда вместе с полосой чисел он не влезает, на следующую
            // строку переносится ПОЛОСА, а не текст по паре букв («— П / о / д…»).
            <span className="min-w-[9rem] flex-[1_1_9rem] break-words">{l.text}</span>
          )}
          <SoundFacts line={l} />
          <VoiceParts line={l} />
        </li>
      ))}
    </ol>
  );
}

/** Где сейчас звук относительно строк списка — всегда на ЛЕВОМ КРАЮ.
 *
 *  Внутри строки — полоса слева во всю её высоту. Между строками (тишина после
 *  реплики) — точка на том же левом краю, на стыке строк. Горизонтальной черты
 *  через строку больше нет: переход «полоса ↔ черта» выглядел пляской (владелец,
 *  15.09 Q8 и 16.09 AB3: «должно идти только по левому краю всё»). Точка стоит всю
 *  паузу, до начала следующей строки. */
function PlayMark({ on, inside }: { on: boolean; inside: boolean }) {
  if (!on) return null;
  return inside ? (
    <span className="pointer-events-none absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary" />
  ) : (
    <span className="pointer-events-none absolute -bottom-[3px] -left-[2px] size-1.5 rounded-full bg-primary" />
  );
}

/** Голоса внутри одной реплики, когда разрезать не решились, а их там явно два. */
function VoiceParts({ line }: { line: TranscriptLine }) {
  const { t } = useT();
  const parts = line.parts ?? [];
  if (parts.length < 2) return null;
  return (
    <div className="mt-0.5 pl-12 text-[11px] italic text-muted-foreground/70">
      {t('conversations.transcript.parts')}:{' '}
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && '; '}
          «{(p.text || '').trim()}»
        </span>
      ))}
    </div>
  );
}

/**
 * The conversation itself: player on top, dialogue under it.
 *
 * Every number here comes from the backend on purpose. The model used to write
 * timecodes itself and got them wrong almost every time, so it now reports
 * «clip N, second M inside it» and the backend does the addition. `t` is
 * seconds from the START OF THE CONVERSATION, which begins mid-clip — hence
 * `offset`, the gap between the player's zero (first clip's start) and that.
 *
 * Interactions, all requested by the owner:
 *   · the current line highlights as it plays, with a pointer in the margin;
 *   · click a TIMECODE — the player jumps there;
 *   · click a LINE — the raw ASR text of its clip unfolds under it;
 *   · guessed lines are grey: the model filled them in, they were not heard.
 * Raw and restored are ONE field with a switch, not two blocks: the restored
 * dialogue is what a person reads, the raw text is for checking it.
 */
function DialogueBoard({ clips, lines, transcript, offset, endSec, rawFallback, fullUrl, q, terms, expanded = true, convId, markup, problems = [] }: {
  /** Только сотрудникам: id разговора и его пометка «для разметки» (L8). */
  convId?: number;
  markup?: Conversation['markup'];
  clips: Clip[];
  lines: DialogueLine[];
  /** Проблемы разговора: у которых известна реплика — значок прямо на ней (AC11). */
  problems?: ConversationProblem[];
  transcript: TranscriptLine[];
  offset: number;
  /** Конец дорожки от нуля плеера: последняя реплика плюс запас. */
  endSec?: number | null;
  rawFallback: string;
  /** Склеенная запись всего разговора; если есть — играем её одним файлом. */
  fullUrl?: string | null;
  /** Запрос из строки поиска — подсветить его вхождения в репликах. */
  q?: string;
  /** Слова, которые назвал бэк как совпавшие: их и подсвечиваем целиком. */
  terms?: string[];
  /** Разговор раскрыт: только тогда плеер тянет метаданные звука. */
  expanded?: boolean;
}) {
  const { t } = useT();
  // Проблемы по номеру реплики: судья отдаёт цитату, бэк переводит её в номер.
  const problemsByLine = useMemo(() => {
    const m = new Map<number, ConversationProblem[]>();
    for (const p of problems) {
      if (p.line == null) continue;
      m.set(p.line, [...(m.get(p.line) ?? []), p]);
    }
    return m;
  }, [problems]);
  // Один файл на разговор — один элемент и одна дорожка: стыков нет вовсе, а
  // время файла и есть время разговора (клипы разложены по времени записи ещё
  // на сервере). Плеер на клипах остаётся для разговоров, собранных раньше.
  // Запасная длительность склеенного файла — по тем же числам, по которым его
  // клеит бэк (glue.build_conversation_audio): от начала первого клипа до конца
  // самого позднего, с обрезом на endSec. Нужна Safari: у .ogg он длительность
  // не знает, пока не скачает файл целиком (duration = Infinity), и на iPhone
  // стояло 0:00.0/0:00.0 без дорожки, хотя звук играл (Эдем, 14.09).
  //
  // ТОЛЬКО запасная, не главная. Сверка с ffprobe на пяти последних разговорах:
  // у двух совпадение до 0.01 с, у трёх расхождение 22-30 с — файл склеен до
  // появления обреза или состав клипов поменялся после склейки. Где браузер
  // длительность знает, верим файлу.
  const fullDuration = (() => {
    if (!fullUrl) return null;
    const timed = clips.filter((c) => c.recorded_at && c.duration);
    if (!timed.length) return null;
    const base = Math.min(...timed.map((c) => Date.parse(c.recorded_at as string)));
    const raw = Math.max(...timed.map((c) => (Date.parse(c.recorded_at as string) - base) / 1000 + (c.duration as number)));
    return endSec != null && endSec > 0 ? Math.min(raw, endSec) : raw;
  })();
  // Файл разговора в память (AB9, lib/conversationAudio.ts). Качается, когда ряд
  // раскрыли; пока не пришёл — играем по ссылке на хранилище, как раньше.
  // Подменяем только на паузе: смена ссылки пересоздаёт элемент, на ходу звук бы
  // оборвался.
  const [memUrl, setMemUrl] = useState<string | null>(null);
  const [memUsed, setMemUsed] = useState(false);
  useEffect(() => {
    setMemUrl(null);
    setMemUsed(false);
  }, [convId]);
  useEffect(() => {
    if (!expanded || !fullUrl || convId == null || memUrl) return;
    let alive = true;
    loadConversationAudio(convId).then((a) => { if (alive) setMemUrl(a.url); }).catch(() => undefined);
    return () => { alive = false; };
  }, [expanded, fullUrl, convId, memUrl]);
  const { total, elapsed, playing, waiting, toggle, seekTo, ticks, element, idle, starved } = useClipPlayer(
    fullUrl ? [{ sequence_number: 0, audio_url: (memUsed && memUrl) || fullUrl, fallbackDuration: fullDuration }] : clips,
    expanded,
  );
  // Когда подменять: звук не идёт и не просили — сразу; звук идёт ровно — на
  // ближайшей паузе (пересоздание на ходу оборвало бы звук, а на iPhone play()
  // вне нажатия и вовсе отклоняется); просили играть, но данных нет — сразу, с
  // того же места: это и есть зависший запрос к хранилищу.
  const idleRef = useRef(idle);
  idleRef.current = idle;
  const starvedRef = useRef(starved);
  starvedRef.current = starved;
  useEffect(() => {
    if (!memUrl || memUsed) return;
    const check = () => {
      if (idleRef.current() || starvedRef.current()) setMemUsed(true);
    };
    check();
    const id = window.setInterval(check, 500);
    return () => window.clearInterval(id);
  }, [memUrl, memUsed, playing]);
  const [mode, setMode] = useState<'restored' | 'raw'>('restored');
  // Сырой текст распознавания — третий, служебный вид: в настройках плеера.
  const [plain, setPlain] = useState(false);
  // «Играть один сегмент»: клик по реплике или отрезку играет только его и
  // останавливается на его конце (владелец, 14.09). Настройка зрителя — в
  // localStorage, со страховкой: в приватном окне хранилище бросает.
  const [oneSeg, setOneSegState] = useState<boolean>(() => {
    try { return localStorage.getItem('conversations.oneSegment') === '1'; } catch { return false; }
  });
  const setOneSeg = (v: boolean) => {
    setOneSegState(v);
    try { localStorage.setItem('conversations.oneSegment', v ? '1' : '0'); } catch { /* без хранилища — только на эту страницу */ }
  };
  const [settingsOpen, setSettingsOpen] = useState(false);
  // «Играть один сегмент» — ТОЧНЫМ куском записи, а не перемоткой и паузой у <audio>:
  // так конец отрезка каждый раз разный (замер 15.09: от −26 до +22 мс даже в пустом
  // браузере, на живой странице больше), и слышно соседний слог. Файл разговора
  // качается и декодируется один раз, когда режим включён у раскрытого разговора;
  // пока не готово — играем по-старому. Подробности — lib/exactPlayer.ts.
  const exact = useExactRecording(
    fullUrl && convId != null ? `conv-${convId}` : null,
    () => loadConversationAudio(convId as number).then((a) => a.blob),
    expanded && oneSeg,
  );
  // Чей звук последний: бегунок и подсветка берут позицию у того, кто играл.
  const [lastExact, setLastExact] = useState(false);
  // «Пометить для разметки» (L8): кладёт разговор в очередь «Данные → Аудио → Диалоги».
  // markup === undefined — зритель не сотрудник, переключателя нет.
  const [flagged, setFlagged] = useState<boolean>(!!markup?.flagged);
  const toggleFlag = (v: boolean) => {
    if (convId == null) return;
    setFlagged(v);
    flagConversation(convId, v).catch(() => setFlagged(!v));
  };
  const settingsRef = useRef<HTMLButtonElement | null>(null);
  const [opened, setOpened] = useState<number | null>(null);

  // Ноль плеера — начало ПЕРВОГО КЛИПА, а ноль разговора наступает позже:
  // разговор начинается посреди клипа (у 19418 это 12.3 с). Раньше время в
  // тексте отсчитывалось от разговора, а дорожка — от клипа, и клик на «0:02»
  // уезжал на двенадцать секунд в тишину. Здесь весь плеер живёт в СЕКУНДАХ
  // РАЗГОВОРА: начало трека сдвинуто, длительность урезана, клики пересчитаны.
  const at = (lastExact ? exact.pos : elapsed) - offset;
  // Дорожка кончается там, где кончился разговор, а не там, где кончился
  // последний клип: иначе полминуты пустоты после ухода гостя.
  const trackEnd = endSec != null && endSec > offset ? Math.min(total, endSec) : total;
  const span = Math.max(0, trackEnd - offset);
  const donePct = span > 0 ? Math.min(100, Math.max(0, (at / span) * 100)) : 0;
  const seekRel = (sec: number, autoplay = false, hold = false, stop: number | null = null) =>
    seekTo(offset + Math.max(0, sec), autoplay, hold, stop == null ? null : offset + stop);
  // Дорожка кончается вместе с разговором — и звук тоже: файл может быть
  // длиннее (склеен до конца клипа), а бегунок упирался в край и стоял, пока
  // звук шёл дальше. Останавливаем один раз при пересечении конца, а не на
  // каждом кадре: иначе «play» у конца дорожки тут же гасился обратно.
  const stoppedAtEnd = useRef(false);
  useEffect(() => {
    if (span <= 0) return;
    if (at < span - 0.5) stoppedAtEnd.current = false;
    if (playing && at >= span && !stoppedAtEnd.current) {
      stoppedAtEnd.current = true;
      toggle();
    }
  }, [at, playing, span, toggle]);
  // Засечки на дорожке — по ТОМУ ЖЕ списку, что показан рядом: в режиме «как
  // распознано» метки восстановленного диалога стояли не там, где строки под
  // ними, и дорожка спорила со списком.
  const marks: { t: number; dur: number; kind: 'cashier' | 'guest' | 'event' | 'gap' | 'prior' }[] =
    mode === 'raw'
      ? transcript
          .filter((l) => (l.text || '').trim() || l.tone || l.gap)
          .map((l) => ({
            t: l.t ?? 0,
            dur: l.dur ?? 0.6,
            kind: (l.tone ? 'event' : l.gap ? 'gap' : 'guest') as 'event' | 'gap' | 'guest',
          }))
      : lines.map((l, i) => ({
          t: l.prior ? -1 : l.t,
          // У паузы без своих строк длины нет — она тянется до следующей строки.
          dur: l.prior ? 0 : l.speaker === 'pause'
            ? (l.dur ?? Math.max(0.3, (lines[i + 1]?.t ?? l.t + 1) - l.t))
            : (l.dur ?? 0.8),
          // Реплика из чужого клипа (#1396): на дорожке её нет и текущей она не бывает.
          kind: (l.prior ? 'prior' : l.speaker === 'event' ? 'event'
            : l.speaker === 'pause' ? 'gap'
            : l.speaker === 'cashier' ? 'cashier' : 'guest') as 'event' | 'cashier' | 'guest' | 'gap' | 'prior',
        }));

  // Разметка стык в стык (X11): щель короче 0.35 с до следующего отрезка —
  // это не пауза (паузы от 0.3 с идут своей строкой), а шаг сетки кадров; на
  // дорожке дотягиваем отрезок до соседа, чтобы не было белых огрызков.
  const drawDur = (i: number) => {
    const l = marks[i];
    const next = marks.slice(i + 1).find((m) => m.kind !== 'event');
    if (l.kind === 'event' || !next) return l.dur;
    const gap = next.t - (l.t + l.dur);
    return gap > 0 && gap < 0.35 ? next.t - l.t : l.dur;
  };

  // Active line = the last one that has already started. Linear scan: a
  // conversation is a couple of dozen lines, and binary search here would be
  // more code than it saves.
  let active = -1;
  for (let i = 0; i < marks.length; i++) if (marks[i].kind !== 'prior' && marks[i].t <= at + 0.25) active = i;
  // Звук ещё внутри этой строки или уже в тишине после неё. Небольшой запас,
  // чтобы на стыке двух строк отметка не мигала полосой и точкой.
  const inside = active >= 0 && at <= marks[active].t + (marks[active].dur || 0.8) + 0.3;

  // Плеер, оставленный сам по себе, начинает с края клипа — до начала разговора
  // там чужой хвост. Ставим воспроизведение на начало разговора при первом
  // нажатии, а не при монтировании: до нажатия трогать аудио нечем.
  const started = useRef(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  // Общая подсветка списка и дорожки: наводишь на реплику — светится её отрезок
  // на дорожке, наводишь на отрезок — светится реплика в списке.
  const [hover, setHover] = useState<number | null>(null);

  // Швы клипов в процентах ОТ РАЗГОВОРА (ticks приходят в процентах от всей
  // склейки клипов, а разговор начинается позже и кончается раньше).
  const clipSeams = total > 0
    ? ticks
        .map((left) => (((left / 100) * total - offset) / (span || 1)) * 100)
        .filter((p) => p > 0 && p < 100)
    : [];

  /** Доля дорожки под курсором/пальцем, с зажимом в края. */
  function fractionAt(clientX: number): number {
    const r = barRef.current?.getBoundingClientRect();
    if (!r || r.width <= 0) return 0;
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  }

  // Перетаскивание ползунка. Слушатели вешаются на ОКНО, а не на дорожку:
  // курсор во время перетаскивания уходит за её пределы, и без этого ползунок
  // залипал на краю. setPointerCapture не годится — дорожка перерисовывается по
  // ходу воспроизведения. Пока тянем, ставим позицию без автозапуска, а играть
  // отдаём на отпускание: перезапускать звук на каждый пиксель — это и есть те
  // самые лаги.
  useEffect(() => {
    if (!dragging) return;
    const move = (e: PointerEvent) => seekRel(fractionAt(e.clientX) * span, false, true);
    const up = (e: PointerEvent) => {
      seekRel(fractionAt(e.clientX) * span, true);
      setDragging(false);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  });
  // Конец разговора — конец воспроизведения (Y19): дорожка обрезана по разговору,
  // а файл нет — у склеенных до 14.09 и у цепочки сырых клипов после ухода гостя
  // идёт начало следующего («Можно латте мне сделать», владелец 14.09).
  useEffect(() => {
    if (playing && trackEnd < total - 0.3 && elapsed >= trackEnd) toggle();
  }, [elapsed, playing, trackEnd, total, toggle]);
  /** Клик по реплике или отрезку: слушать с этого места — или только этот сегмент. */
  function playSeg(t0: number, dur?: number | null) {
    // Ровно длина отрезка: прежний минимум 0.3 с у слова в 0.1 с доигрывал соседа.
    const end = t0 + Math.max(0.12, dur ?? 0.8);
    if (oneSeg && exact.ready) {
      if (playing) toggle();
      setLastExact(true);
      exact.play(offset + Math.max(0, t0), offset + end);
      return;
    }
    exact.stop();
    setLastExact(false);
    seekRel(t0, true, false, oneSeg ? end : null);
  }
  function play() {
    if (exact.playing) {
      exact.stop();
      return;
    }
    if (lastExact) {
      // После отрезка «play» продолжает с того места, где отрезок кончился.
      setLastExact(false);
      started.current = true;
      seekRel(Math.max(0, exact.pos - offset), true);
      return;
    }
    if (!started.current && at < 0) {
      started.current = true;
      seekRel(0, true);
      return;
    }
    started.current = true;
    // У конца дорожки «play» значит «сначала», а не бесконечная пауза.
    if (!playing && span > 0 && at >= span - 0.05) {
      seekRel(0, true);
      return;
    }
    toggle();
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      {/* Плеер — верхняя строка той же карточки, что и реплики: одна рамка на
          всё, без своей обводки. Переключатель вида стоит НАД списком, потому что
          управляет списком, а не плеером — в строке плеера он читался как его
          настройка. */}
      <div className="flex items-center gap-3 px-3 pb-1 pt-2.5">
        <button
          type="button"
          onClick={play}
          aria-label={t('conversations.recording.clip')}
          className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105"
        >
          {waiting && !exact.playing ? <Loader2 className="size-4 animate-spin" />
            : playing || exact.playing ? <Pause className="size-4" /> : <Play className="size-4 translate-x-px" />}
        </button>
        <RateButton />
        <div
          ref={barRef}
          className={`relative h-7 min-w-0 flex-1 touch-none select-none ${dragging ? 'cursor-grabbing' : 'cursor-pointer'}`}
          onPointerDown={(e) => {
            if (span <= 0) return;
            e.preventDefault();
            exact.stop();
            setLastExact(false);
            setDragging(true);
            seekRel(fractionAt(e.clientX) * span, false, true);
          }}
        >
          <div className="absolute inset-x-0 top-[14px] h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary/70" style={{ width: `${donePct}%` }} />
            {/* Стыки клипов — прорезь цветом фона ВНУТРИ полосы, а не палка над
                ней: прежние засечки лезли поверх меток реплик и спорили с ними за
                внимание. Это внутренняя диагностика, ей и место в фоне. */}
            {clipSeams.map((left, i) => (
              <div key={`s${i}`} className="absolute inset-y-0 w-0.5 bg-card"
                   style={{ left: `${left}%` }} />
            ))}
          </div>
          {/* Where each line starts — the dialogue laid over the track. */}
          {/* Не засечки, а ОТРЕЗКИ: реплика и звук занимают на дорожке ровно
              столько, сколько длились, и лежат полосой НАД шкалой
              воспроизведения, чтобы не мешаться с ней. Ховер общий: наводишь на
              реплику в списке — светится отрезок, и наоборот. */}
          {/* Отрезки живут в своём слое с обрезкой по краям дорожки; ползунок —
              снаружи, иначе его срезало пополам в начале и в конце. */}
          <div className="absolute inset-x-0 top-0 h-2.5 overflow-hidden">
          {span > 0 && marks.map((l, i) => l.kind === 'prior' ? null : (
            <Tooltip key={i} content={fmtClock(l.t)}><div
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover((h) => (h === i ? null : h))}
              onClick={(e) => { e.stopPropagation(); playSeg(l.t, l.dur); }}
              role="presentation"
              // Полоса отрезков в цветах интерфейса: сотрудник — основной цвет,
              // гость — нейтральный, оплата — янтарный, промежутки — едва
              // заметные. Высота у всех одна, ширина — сколько длилось, но не
              // тоньше, чем можно попасть мышью: иначе это не полоса, а пятна.
              // Плоско и по длительности: никаких скруглений и обводок, ширина —
              // ровно сколько звучало (минимум два пикселя, чтобы было куда
              // навести мышь), цвета — из интерфейса.
              className={`absolute top-0 h-2.5 transition-colors ${
                l.kind === 'event' ? 'bg-amber-500'
                  : l.kind === 'gap' ? ((i === active && inside) || i === hover ? 'bg-foreground/25' : 'bg-foreground/10')
                    : l.kind === 'cashier'
                      ? ((i === active && inside) || i === hover ? 'bg-primary' : 'bg-primary/55')
                      : ((i === active && inside) || i === hover ? 'bg-foreground/65' : 'bg-foreground/30')
              }`}
              style={{
                left: `${Math.min(100, Math.max(0, (l.t / span) * 100))}%`,
                // Отрезок не вылезает за конец дорожки: раньше хвост последней
                // реплики торчал чёрной полоской за плеером.
                width: `${Math.max(0, Math.min(100 - Math.min(100, Math.max(0, (l.t / span) * 100)), ((drawDur(i) || 0.3) / span) * 100))}%`,
                minWidth: '2px',
              }}
            /></Tooltip>
          ))}
          </div>
          {/* Clip seams — faint on purpose: internal diagnostic. */}
          <div
            className={`absolute top-[17px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary shadow transition-transform ${dragging ? 'size-4 cursor-grabbing' : 'size-3 cursor-grab'}`}
            style={{ left: `${donePct}%` }}
          />
        </div>
        <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
          {fmtClock(Math.max(0, at), true)}/{fmtClock(span, true)}
        </span>
        {element}
      </div>
      <div className="flex items-end gap-4 border-b border-border px-3">
        {(['restored', 'raw'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`-mb-px border-b-2 py-1.5 text-[12px] font-medium transition-colors ${
              mode === m ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(m === 'restored' ? 'conversations.dialogue.restored' : 'conversations.dialogue.raw')}
          </button>
        ))}
        {/* Настройки плеера — шестерёнка справа: служебные переключатели не
            спорят с вкладками за внимание. */}
        <Tooltip content={t('conversations.dialogue.settings')}><button
          ref={settingsRef}
          type="button"
          onClick={() => setSettingsOpen((o) => !o)}
          aria-label={t('conversations.dialogue.settings')}
          className={`ml-auto mb-1 flex size-6 items-center justify-center rounded-full transition-colors ${
            settingsOpen ? 'bg-muted text-foreground' : 'text-muted-foreground/60 hover:bg-muted hover:text-foreground'
          }`}
        >
          <Settings2 className="size-3.5" />
        </button></Tooltip>
        <Popover open={settingsOpen} onOpenChange={setSettingsOpen} triggerRef={settingsRef} align="end"
                 className="w-64 rounded-lg border border-border bg-card p-1 shadow-lg">
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 py-2 text-[12px] hover:bg-muted/50">
            <span>{t('conversations.dialogue.showPlain')}</span>
            <Switch
              checked={plain}
              onCheckedChange={(v) => { setPlain(v); if (v) setMode('raw'); }}
              aria-label={t('conversations.dialogue.showPlain')}
              // Выключенный переключатель на белой карточке почти не виден —
              // даём дорожке заметный серый.
              className={plain ? '' : 'bg-foreground/15'}
            />
          </label>
          <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 py-2 text-[12px] hover:bg-muted/50">
            <span className="flex items-center gap-1.5">
              {t('conversations.dialogue.oneSegment')}
              <Tooltip content={t('conversations.dialogue.oneSegmentHint')} side="bottom" className="max-w-56 text-[11px]">
                <Info className="size-3.5 text-muted-foreground/60" />
              </Tooltip>
            </span>
            <Switch checked={oneSeg} onCheckedChange={setOneSeg} aria-label={t('conversations.dialogue.oneSegment')}
                    className={oneSeg ? '' : 'bg-foreground/15'} />
          </label>
          {markup !== undefined && convId != null && (
            <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 py-2 text-[12px] hover:bg-muted/50">
              <span>{t('conversations.dialogue.flagForMarkup')}</span>
              <Switch checked={flagged} onCheckedChange={toggleFlag} aria-label={t('conversations.dialogue.flagForMarkup')}
                      className={flagged ? '' : 'bg-foreground/15'} />
            </label>
          )}
        </Popover>
      </div>

      <div className="px-1.5 py-2">
      {mode === 'raw' ? (
        <>
          {plain ? (
            // Совсем сырой текст: как его отдало распознавание, без разбора на
            // реплики. Нужен редко — свериться, не потерял ли разбор слов, —
            // поэтому и прячется за неприметной ссылкой.
            <p className="whitespace-pre-wrap break-words text-muted-foreground">
              {rawFallback || t('conversations.internal.empty')}
            </p>
          ) : (
            <TranscriptView lines={transcript} fallback={rawFallback} onSeek={playSeg}
                            active={active} inside={inside} />
          )}
        </>
      ) : (
        <ol className="space-y-0.5">
          {lines.map((l, i) => l.speaker === 'pause' || l.speaker === 'event' ? (
            // Пауза и событие записи (оплата, скан, звон, печать чека) — строки того
            // же вида, что реплики: подпись «что · когда» тем же шрифтом и отступом,
            // клик ставит звук на их время, отметка позиции ходит и по ним (AB2,
            // владелец 16.09). От реплик отличаются значком и тем, что у них нет
            // подложки говорящего: это не чьи-то слова (P1, 14.09).
            <li key={i} className="relative">
              <Tooltip content={t('conversations.dialogue.seekHint')}><div
                role="button"
                tabIndex={0}
                onClick={() => playSeg(l.t, marks[i]?.dur)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    playSeg(l.t, marks[i]?.dur);
                  }
                }}
                className="relative block cursor-pointer rounded px-2.5 py-1.5 transition-colors hover:bg-foreground/[0.04]"
              >
                <PlayMark on={i === active} inside={inside} />
                <div className="flex items-baseline gap-1.5 text-[11px] leading-4">
                  {l.speaker === 'pause' ? (
                    <Hourglass className="size-3 shrink-0 self-center text-muted-foreground/70" />
                  ) : l.event === 'чек' ? (
                    <Printer className="size-3 shrink-0 self-center text-amber-600/80 dark:text-amber-500/80" />
                  ) : l.event === 'скан' ? (
                    <Nfc className="size-3 shrink-0 self-center text-amber-600/80 dark:text-amber-500/80" />
                  ) : l.event === 'звон' ? (
                    <Bell className="size-3 shrink-0 self-center text-amber-600/80 dark:text-amber-500/80" />
                  ) : (
                    <CreditCard className="size-3 shrink-0 self-center text-amber-600/80 dark:text-amber-500/80" />
                  )}
                  <span className={l.speaker === 'pause'
                    ? 'font-medium text-muted-foreground'
                    : 'font-medium text-amber-700/90 dark:text-amber-500/90'}>
                    {t(l.speaker === 'pause' ? 'conversations.dialogue.pauseTitle'
                      : l.event === 'скан' ? 'conversations.dialogue.event.scan'
                      : l.event === 'звон' ? 'conversations.dialogue.event.chime'
                      : l.event === 'чек' ? 'conversations.dialogue.event.print'
                      : 'conversations.dialogue.event.payment')}
                  </span>
                  <span className="text-muted-foreground/50">·</span>
                  <span className="font-mono tabular-nums text-muted-foreground">{fmtClock(l.t, true)}</span>
                  {l.speaker === 'pause' ? (
                    <span className="tabular-nums text-muted-foreground/70">
                      {t('conversations.dialogue.pauseSec', { sec: (marks[i]?.dur ?? 0).toFixed(1) })}
                    </span>
                  ) : l.event !== 'звон' && l.event !== 'чек' && l.hz ? (
                    <span className="tabular-nums text-muted-foreground/70">
                      {t('conversations.dialogue.event.hz', { hz: String(l.hz) })}
                    </span>
                  ) : null}
                </div>
                {l.speaker === 'pause' && l.text && l.text.trim().toLowerCase() !== 'пауза' && (
                  <div className="mt-0.5 text-[13px] italic text-muted-foreground">{l.text}</div>
                )}
              </div></Tooltip>
            </li>
          ) : l.prior && /^\W*неразборчиво\W*$/i.test(l.text || '') ? null : (
            <li key={i} className="relative">
              {/* Подпись «Сказано до начала этой записи…» клиенту не показываем (Алексей 03.11 #3549);
                  «неразборчиво» из хвоста прошлой записи — тоже: это шум, а не реплика (#3551). */}
              {/* Разворачивает исходник вся строка, а не только слова: попадать
                  мышью в текст реплики неудобно, особенно короткой. Время —
                  исключение, у него своё действие. */}
              <Tooltip content={l.prior ? undefined : t('conversations.dialogue.seekHint')}><div
                role="button"
                tabIndex={0}
                // Клик по карточке — слушать с этого места; исходный текст —
                // отдельной микро-кнопкой, которая появляется по наведению.
                onClick={l.prior ? undefined : () => playSeg(l.t, marks[i]?.dur)}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h) => (h === i ? null : h))}
                onKeyDown={(e) => {
                  if (!l.prior && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    playSeg(l.t, marks[i]?.dur);
                  }
                }}
                // Роль различаем фоном, текущую реплику — РАМКОЙ. Пока текущая
                // подсвечивалась заливкой, две раскраски спорили друг с другом:
                // непонятно, это реплика сотрудника или та, что играет сейчас.
                // Вид переписки, а не таблицы: мелкая подпись «кто · когда» над
                // репликой, сама реплика — обычным текстом на всю ширину. Три
                // колонки (время | роль | текст) читались как таблица в экселе.
                // Роль различаем подложкой, текущую реплику — рамкой.
                // Реплика — не карточка: без рамок, только лёгкая подложка у
                // сотрудника и полоса слева у той, что играет сейчас.
                className={`group/line relative block rounded px-2.5 py-1.5 transition-colors ${
                  l.prior ? 'cursor-default text-muted-foreground opacity-70'
                    : l.speaker === 'cashier' ? 'cursor-pointer bg-primary/[0.06] hover:bg-primary/[0.09]'
                    : 'cursor-pointer hover:bg-foreground/[0.04]'
                }`}
              >
                <PlayMark on={i === active} inside={inside} />
                <div className="mb-0.5 flex items-baseline gap-1.5 text-[11px] leading-4">
                  {/* Значок проблемы на самой реплике, где она прозвучала
                      (владелец, 16.09 AC11), с тем же пояснением в подсказке,
                      что и в разборе справа. */}
                  {(problemsByLine.get(i) ?? []).map((p, k) => (
                    <Tooltip key={`p${k}`} className="max-w-64 text-[11px]" content={
                      <span>
                        <span className="font-medium">
                          {t(`conversations.problem.kind.${p.kind}`)} · {t(`conversations.problem.severity.${p.severity}`)}
                        </span>
                        {': '}{p.text}
                      </span>
                    }>
                      <AlertTriangle
                        className={`size-3 shrink-0 self-center ${p.severity === 'high' ? 'text-red-600'
                          : p.severity === 'medium' ? 'text-orange-500' : 'text-yellow-500'}`}
                      />
                    </Tooltip>
                  ))}
                  <span className={l.speaker === 'cashier'
                    ? 'font-medium text-primary/90'
                    : 'font-medium text-muted-foreground'}>
                    {t(l.speaker === 'cashier' ? 'conversations.dialogue.cashier' : 'conversations.dialogue.guest')}
                  </span>
                  {!l.prior && (
                    <>
                      <span className="text-muted-foreground/50">·</span>
                      <span className="font-mono tabular-nums text-muted-foreground">{fmtClock(l.t, true)}</span>
                    </>
                  )}
                </div>
                {/* Исходник открывается кликом ровно по СЛОВАМ. Кликабельный
                    span — строчный (inline), поэтому область клика и ховера —
                    сами слова, а не вся ширина строки: блочный span тянулся
                    до правого края, и пустое место справа от слов тоже
                    открывало исходник. На наведение слова подсвечиваются
                    подложкой и пунктиром; клик мимо слов — слушать с этого
                    места. */}
                <p className="text-[13px] leading-5">
                <Tooltip content={t('conversations.dialogue.sourceHint')}><span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setOpened(opened === i ? null : i); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      setOpened(opened === i ? null : i);
                    }
                  }}
                  className={`inline cursor-pointer break-words rounded-sm decoration-dotted underline-offset-4 [box-decoration-break:clone] hover:bg-primary/10 hover:[text-decoration-line:underline] ${
                    opened === i ? 'bg-primary/10 [text-decoration-line:underline]' : ''
                  } ${
                    // Служебная метка (‹неразборчивая речь›) — это не слова
                    // человека, а описание звука: рисуем её как метку, а не как
                    // реплику в кавычках.
                    /^[‹<][^‹›<>]+[›>]$/.test((l.text || '').trim())
                      ? 'italic text-muted-foreground/60'
                      : l.guessed
                      ? 'italic text-muted-foreground/70'
                      : l.partial
                        // Разобрано наполовину: слова в записи есть, но часть
                        // достроена по смыслу. Не серое, как выдуманное, и не
                        // обычное, как расслышанное, — подчёркнуто пунктиром.
                        ? 'text-foreground/80 decoration-dotted underline-offset-4 [text-decoration-line:underline]'
                        : 'text-foreground'
                  }`}
                >
                  {/^[‹<][^‹›<>]+[›>]$/.test((l.text || '').trim())
                    ? (l.text || '').trim().replace(/^[‹<]|[›>]$/g, '').toLowerCase()
                    : <Hit text={l.text} q={q} terms={terms} />}
                </span></Tooltip>
                </p>
              </div></Tooltip>
              {opened === i && (
                // Не голый текст, а наш формат: как эту реплику услышали и по
                // каким числам решили, кто её сказал. Спрятать назад — явным
                // крестиком в углу (и повторным кликом по словам).
                <div className="relative ml-2.5 mt-0.5 space-y-0.5 rounded bg-muted/50 py-1.5 pl-2 pr-7 text-xs">
                  {/* Свернуть — иконкой «свернуть», серой и в правом нижнем углу
                      панели: крестик читался как «удалить» и стоял где попало
                      (владелец, 14.09). */}
                  <Tooltip content={t('conversations.dialogue.hideSource')}><button
                    type="button"
                    onClick={() => setOpened(null)}
                    aria-label={t('conversations.dialogue.hideSource')}
                    className="absolute bottom-1 right-1 flex size-5 items-center justify-center rounded text-muted-foreground/50 hover:bg-muted hover:text-muted-foreground"
                  >
                    <ChevronsDownUp className="size-3.5" />
                  </button></Tooltip>
                  {l.source?.gap ? (
                    // Слов в этот момент не было — показываем, что звучало.
                    <p className="italic text-muted-foreground">
                      {t(l.source.gap === 'речь' ? (l.source.quiet ? 'conversations.transcript.unclearFar' : 'conversations.transcript.unclear')
                        : l.source.gap === 'вставка' ? (l.source.hz2 ? 'conversations.transcript.insert' : 'conversations.transcript.murmur')
                        : l.source.gap === 'шум' ? 'conversations.transcript.noise'
                        : l.source.gap === 'стук' ? 'conversations.transcript.taps'
                        : 'conversations.transcript.silence',
                        { sec: (l.source.dur ?? 0).toFixed(1), hz: String(l.source.hz2 ?? '') })}
                    </p>
                  ) : l.sources && l.sources.length > 1 ? (
                    // Реплика собрана из нескольких строк — показываем все, по
                    // порядку, каждую со своими числами звука.
                    l.sources.map((src, k) => (
                      <div key={k}>
                        <p className="whitespace-pre-wrap break-words text-muted-foreground">
                          {src.text || t('conversations.internal.empty')}
                        </p>
                        <SoundFacts line={src} />
                      </div>
                    ))
                  ) : l.source ? (
                    <>
                      <p className="whitespace-pre-wrap break-words text-muted-foreground">
                        {l.source.text || t('conversations.internal.empty')}
                      </p>
                      <SoundFacts line={l.source} />
                    </>
                  ) : (
                    // Домысленная реплика: в расшифровке её нет, и показывать
                    // чужую соседнюю строку — обманывать.
                    <p className="italic text-muted-foreground">
                      {t('conversations.dialogue.noSource')}
                    </p>
                  )}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      </div>
    </div>
  );
}

/** «14:31:07» — время суток С СЕКУНДАМИ, опционально сдвинутое на N секунд.
 *
 * Нужно ровно там, где спрашивают про стыки: между «14:31» и «14:30» может быть
 * и ноль секунд, и десять, и по минутам этого не увидеть.
 */

/** "1:07" — conversations are minutes long, so mm:ss is the whole story. */
function fmtClock(sec: number, tenths = false): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  // Десятые доли — по просьбе владельца: реплики стоят с точностью до слова,
  // и «0:02» для двух соседних строк ничего не различает.
  if (tenths) return `${m}:${s < 10 ? '0' : ''}${s.toFixed(1)}`;
  return `${m}:${String(Math.floor(s)).padStart(2, '0')}`;
}

/**
 * Чек-лист ПО ЭТАПАМ разговора.
 *
 * Раньше пункты стояли по темам, и по ним нельзя было увидеть главное: прошло
 * ли обслуживание свои стадии. Владелец перестроил это на последовательность —
 * поздоровался, принял заказ, предложил дополнительно, спросил про программу,
 * назвал сумму и принял оплату, завершил, — а вежливость с настроением
 * отправил вниз. Стадии обязательны, но закрывать их можно как угодно
 * неформально: важно, что стадия пройдена, а не что сказано по скрипту.
 *
 * ЗНАЧКИ отвечают на «было или не было», ЦВЕТ — «хорошо, плохо или норма»:
 *   зелёная галка — случилось хорошее;
 *   серая галка   — было, и это норма: чекпоинт пройден, хвалить не за что;
 *   серый крест   — не было, хотелось бы, но не страшно;
 *   серый прочерк — не было, и это ни хорошо ни плохо;
 *   красный крест — случилось плохое.
 * У негативных пунктов рядом со значком стоит слово, чтобы не было двойных
 * трактовок: «да» с красным крестом, «нет» с серой галкой.
 *
 * INFO-пункты описывают разговор, а не работу сотрудника: они собраны в
 * отдельную подсекцию и в подсчёт не идут.
 */
type MarkKind = 'good' | 'norm' | 'bad' | 'neutral' | 'info';

/** Ключ пункта: поле ответа модели или синтетическое поле интерфейса
 *  (например, «тип апселла» — одно поле, собранное из четырёх флагов). */
type ItemKey = keyof ConversationRawLlmResponse | 'upsell_types';

/** Значение поля ответа по ключу пункта; синтетических полей в ответе нет. */
const itemValue = (raw: ConversationRawLlmResponse, key: ItemKey): unknown =>
  (raw as unknown as Record<string, unknown>)[key as string];

type Item = {
  key: ItemKey;
  kind: MarkKind;
  /** Пункт неприменим — уезжает в свёрнутое «не оценивалось». */
  naWhen?: (r: ConversationRawLlmResponse) => boolean;
  /** Значение показывается текстом, а не значком. */
  asText?: boolean;
  /** Одно поле со множественным выбором: перечисляем отмеченные из этих ключей. */
  asChoice?: (keyof ConversationRawLlmResponse)[];
  /** ИНФО текстом: своя подпись для «да» и для «нет», без значков и оценки. */
  asWords?: [yes: string, no: string];
  /** Было, но хвалить не за что: серая галка вместо зелёной. */
  normWhen?: (r: ConversationRawLlmResponse) => boolean;
};

/** Типы апселла — одно поле со множественным выбором. */
const UPSELL_TYPES: (keyof ConversationRawLlmResponse)[] = [
  // Виды с 15.09 (владелец): слабые «что-то ещё?» и добавки, конкретные — еда к
  // напитку и позиция интереснее. Старые типы — для записей, разобранных раньше:
  // перечисляются только отмеченные, и в новых записях их нет вовсе.
  'upsell_food', 'upsell_better_choice', 'upsell_anything_else', 'upsell_addons',
  'upsell_complementary', 'upsell_seasonal', 'upsell_margin', 'upsell_daypart', 'upsell_open_question',
];

type Stage = {
  stage: string;
  /** Порядковый номер шага; без номера — «общее настроение», он не шаг. */
  num?: number;
  /** Этап может не случиться вовсе (нет приветствия, нет апселла). Тогда
   *  карточка сворачивается с красным крестом в шапке, а пункты внутри — N/A.
   *  Приём заказа и оплата без этого: без них разговор неполный. */
  optional?: keyof ConversationRawLlmResponse;
  items: Item[];
  info: Item[];
};

/** Пункты, выключенные по умолчанию (не на всех точках есть): включённые в настройках точки,
 *  они неприменимы только по этому условию — иначе «не было» = серый крест (AI373, #2985). */
export const DEFAULT_OFF_ITEMS = new Set<string>(['offered_ceramic']);
const ON_WHEN: Record<string, (r: ConversationRawLlmResponse) => boolean> = {
  offered_ceramic: (r) => r.client_asked_togo === true,
};

export const STAGES: Stage[] = [
  {
    stage: 'greeting', num: 1, optional: 'greeting',
    items: [{ key: 'greeting', kind: 'good' }],
    info: [{ key: 'greeting_informal', kind: 'info', asWords: ['неформальное', 'формальное'],
             naWhen: (r) => r.greeting !== true }],
  },
  {
    stage: 'order', num: 2,
    items: [
      // «Гость назвал объём сам» — служебный параметр, его не показываем
      // никогда: если назвал, вопрос об объёме был не нужен, и оба пункта
      // уходят; если не назвал — всё решает один «Спросил объём».
      { key: 'asked_volume', kind: 'good', naWhen: (r) => r.client_named_volume === true },
      { key: 'asked_here_or_togo', kind: 'good', naWhen: (r) => r.client_asked_togo === true },
      // Керамика есть не на всех точках: по умолчанию пункт виден, только когда
      // предложил; где она есть — включают в Настройки → Анализ речи (ON_WHEN ниже).
      { key: 'offered_ceramic', kind: 'good',
        naWhen: (r) => r.client_asked_togo === true || r.offered_ceramic !== true },
      { key: 'repeated_order', kind: 'good' },
      { key: 'warned_wait_before_pay', kind: 'good' },
      { key: 'own_cup_fulfilled', kind: 'good', naWhen: (r) => r.client_own_cup !== true },
    ],
    info: [
      // «Холодный или горячий» обусловлен тем, бывает ли напиток и таким и
      // таким; этого мы не знаем, поэтому показываем только когда спросил.
      { key: 'asked_hot_cold', kind: 'info', asWords: ['да', 'нет'],
        naWhen: (r) => r.asked_hot_cold !== true },
      { key: 'client_own_cup', kind: 'info', asWords: ['да', 'нет'],
        naWhen: (r) => r.client_own_cup !== true },
    ],
  },
  {
    stage: 'upsell', num: 3, optional: 'upsell_offered',
    items: [
      // Слабое предложение («что-то ещё?», сахар-сироп-корица) этап закрывает,
      // но не хвалится: серая галка (владелец, 15.09).
      { key: 'upsell_offered', kind: 'good', naWhen: (r) => r.upsell_no_chance === true,
        normWhen: (r) => r.upsell_strength === 'weak' },
      { key: 'upsell_filter_instead_americano', kind: 'good',
        naWhen: (r) => !/американо/i.test(`${r.upsell_item || ''} ${r.upsell_better_items || ''}`)
          && r.upsell_filter_instead_americano !== true },
    ],
    info: [
      { key: 'upsell_types', kind: 'info', asChoice: UPSELL_TYPES },
      { key: 'upsell_food_items', kind: 'info', asText: true, naWhen: (r) => !r.upsell_food_items },
      { key: 'upsell_better_items', kind: 'info', asText: true, naWhen: (r) => !r.upsell_better_items },
      // «Предложил сироп» убран: он дублировал «Что предложил» (владелец, 17.09 AI18).
      { key: 'upsell_item', kind: 'info', asText: true },
      { key: 'upsell_accepted', kind: 'info', asWords: ['да', 'нет'] },
    ],
  },
  {
    stage: 'loyalty', num: 4, optional: 'asked_loyalty',
    items: [
      // Отсканированная при кассире лицензия засчитывается (Алексей 03.11 #3565): «да» не прячем в «н/п».
      { key: 'asked_loyalty', kind: 'good', naWhen: (r) => r.loyalty_no_chance === true && r.asked_loyalty !== true },
      { key: 'named_license_surfer', kind: 'good', naWhen: (r) => r.loyalty_no_chance === true && r.named_license_surfer !== true },
      { key: 'offered_loyalty_signup', kind: 'good', naWhen: (r) => r.client_has_loyalty === true },
    ],
    info: [
      { key: 'loyalty_phrase', kind: 'info', asText: true, naWhen: (r) => !r.loyalty_phrase },
      // Гость сам показал лицензию («можете сканировать») — не «спросили» (AI21).
      { key: 'guest_showed_loyalty', kind: 'info', asWords: ['да', 'нет'], naWhen: (r) => r.guest_showed_loyalty == null },
      { key: 'client_has_loyalty', kind: 'info', asWords: ['есть', 'нет'] },
      { key: 'client_agreed_signup', kind: 'info', asWords: ['да', 'нет'],
        naWhen: (r) => r.client_has_loyalty === true },
    ],
  },
  {
    stage: 'payment', num: 5,
    items: [
      { key: 'named_total', kind: 'norm' },
      { key: 'payment_taken', kind: 'norm' },
      { key: 'positive_closing', kind: 'good' },
    ],
    info: [
      { key: 'asked_receipt', kind: 'info', asWords: ['да', 'нет'] },
      { key: 'told_wait_time', kind: 'info', asWords: ['да', 'нет'] },
    ],
  },
  {
    stage: 'manner',
    items: [
      { key: 'active_communication', kind: 'good' },
      { key: 'interesting_communication', kind: 'good' },
      { key: 'friendly', kind: 'good' },
      { key: 'dry_minimal', kind: 'bad' },
      { key: 'rude', kind: 'bad' },
    ],
    info: [{ key: 'address_on_vy', kind: 'info', asWords: ['на «вы»', 'на «ты»'] }],
  },
];

/** Значок и цвет по правилам выше. */
function Mark({ item, raw }: { item: Item; raw: ConversationRawLlmResponse }) {
  const { t } = useT();
  const value = itemValue(raw, item.key) === true;
  if (item.kind === 'bad') {
    // Словом рядом со значком: «да» это плохо, «нет» это норма.
    return value ? (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
        {t('conversations.checklist.yes')}
        <X className="size-3.5" />
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        {t('conversations.checklist.no')}
        <Check className="size-3.5" />
      </span>
    );
  }
  if (value) {
    // Зелёным — только за то, что сделано СВЕРХ обязательного. Пройденный
    // чекпоинт (назвал сумму, принял оплату) отмечается серым: хвалить кассира
    // за принятую оплату бессмысленно, это условие самой работы.
    return item.kind === 'norm' || item.normWhen?.(raw)
      ? <Check className="size-3.5 text-muted-foreground" />
      : <Check className="size-3.5 text-green-600 dark:text-green-400" />;
  }
  // Не случилось. Для «хотелось бы» — крест, для нейтрального — прочерк.
  return item.kind === 'neutral'
    ? <span className="text-muted-foreground">—</span>
    : <X className="size-3.5 text-muted-foreground" />;
}

function ChecklistRow({ item, raw, forceNa }: {
  item: Item; raw: ConversationRawLlmResponse; forceNa?: boolean;
}) {
  const { t } = useT();
  const label = t(`conversations.checklist.item.${item.key}`);
  if (forceNa) {
    return (
      <div className="flex items-baseline justify-between gap-3 py-[3px] text-[13px]">
        <span className="min-w-0 text-muted-foreground/70">{label}</span>
        <span className="shrink-0 text-muted-foreground/60">{t('conversations.checklist.na')}</span>
      </div>
    );
  }
  // Один размер шрифта на всю строку и один вес: разнобой из жирного, мелкого и
  // среднего читался как визуальный шум, а не как таблица.
  return (
    <div className="flex items-baseline justify-between gap-3 py-[3px] text-[13px]">
      <span className="min-w-0 text-muted-foreground">{label}</span>
      {/* Перечисление и текст бывают длинными («еда к напитку, сахар, сироп,
          корица (слабый)»): переносятся справа, а не наезжают на подпись. */}
      <span className={`flex items-center justify-end ${item.asChoice || item.asText
        ? 'min-w-0 max-w-[65%] text-right break-words' : 'shrink-0'}`}>
        {item.asChoice ? (
          <span className="text-foreground/80">
            {item.asChoice.filter((k) => raw[k] === true)
              .map((k) => t(`conversations.checklist.choice.${k}`)).join(', ')
              || <span className="text-muted-foreground">—</span>}
          </span>
        ) : item.asText ? (
          <span className="text-foreground/80">
            {String(itemValue(raw, item.key) || '').trim() || <span className="text-muted-foreground">—</span>}
          </span>
        ) : item.asWords ? (
          // ИНФО словами: факт без значка и без цвета. «Обращение на вы» с
          // зелёной галкой читалось как похвала, хотя это просто наблюдение.
          <span className="text-foreground/80">
            {itemValue(raw, item.key) === true ? item.asWords[0] : item.asWords[1]}
          </span>
        ) : (
          <Mark item={item} raw={raw} />
        )}
      </span>
    </div>
  );
}

/** Одна попытка апселла: вид (с категорией), что предложил, согласие. */
function UpsellAttemptBlock({ attempt, first }: { attempt: UpsellAttempt; first: boolean }) {
  const { t } = useT();
  const row = (label: string, value: React.ReactNode) => (
    <div className="flex items-baseline justify-between gap-3 py-[3px] text-[13px]">
      <span className="min-w-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 max-w-[65%] break-words text-right text-foreground/80">{value}</span>
    </div>
  );
  const kindLabel = t(`conversations.checklist.choice.upsell_${attempt.kind}`);
  const category = attempt.category?.trim();
  return (
    <div className={first ? '' : 'mt-1.5 border-t border-border/60 pt-1.5'}>
      {row(t('conversations.checklist.item.upsell_types'),
        category ? `${kindLabel} · ${category}` : kindLabel)}
      {attempt.items?.trim() && row(
        t(attempt.kind === 'better_choice' ? 'conversations.checklist.item.upsell_better_items'
          : 'conversations.checklist.item.upsell_item'),
        attempt.items.trim())}
      {row(t('conversations.checklist.item.upsell_accepted'), attempt.guest_agreed ? 'да' : 'нет')}
    </div>
  );
}

/** Этапы разговора карточками, инфо отдельно, неприменимое свёрнуто. */
function StageCard({ st, live, liveInfo, raw, absent }: {
  st: Stage; live: Item[]; liveInfo: Item[]; raw: ConversationRawLlmResponse; absent: boolean;
}) {
  const { t } = useT();
  const head = (
    <div className="flex items-baseline gap-2 text-[11px] leading-4">
      {st.num != null && (
        <span className="font-mono text-muted-foreground/60">{st.num}</span>
      )}
      <span className={`font-semibold uppercase tracking-[0.08em] ${
        st.num == null ? 'text-foreground/80' : 'text-muted-foreground/80'
      }`}>
        {t(`conversations.stage.${st.stage}`)}
      </span>
      {absent && (
        // Этапа не было вовсе — красный крест в шапке говорит это сам, а
        // пункты внутри показывать нечем: они все N/A.
        <X className="size-3.5 text-red-600 dark:text-red-400" />
      )}
    </div>
  );
  // Попытки апселла (AB7, владелец 16.09): у каждой свой вид, товары и ответ гостя —
  // под-блоками через тонкий разделитель, без подписи «попытка N». Сводные строки
  // (типы списком, товары, согласие) тогда не нужны: они — сумма тех же попыток.
  const attempts = st.stage === 'upsell' && !absent ? raw.upsell_attempts ?? [] : [];
  const body = (
    <>
      {live.map((i) => <ChecklistRow key={String(i.key)} item={i} raw={raw} forceNa={absent} />)}
      {attempts.length > 0 ? (
        <div className="mt-1">
          {attempts.map((a, k) => <UpsellAttemptBlock key={k} attempt={a} first={k === 0} />)}
        </div>
      ) : liveInfo.length > 0 && (
        <div className="mt-1">
          {liveInfo.map((i) => <ChecklistRow key={String(i.key)} item={i} raw={raw} forceNa={absent} />)}
        </div>
      )}
    </>
  );
  const cls = `mb-2 break-inside-avoid rounded-md px-3 py-2.5 ${
    st.num == null ? 'bg-primary/[0.05]' : 'bg-muted/30'
  }`;
  if (absent) {
    return (
      <details className={cls}>
        <summary className="cursor-pointer list-none">{head}</summary>
        <div className="mt-1.5">{body}</div>
      </details>
    );
  }
  return (
    <div className={cls}>
      <div className="mb-1.5">{head}</div>
      {body}
    </div>
  );
}

/** Модалка «не оценивалось»: один вертикальный скролл, разбитый по этапам. */
function NotRatedDialog({ groups, onClose }: {
  groups: { stage: string; items: Item[] }[]; onClose: () => void;
}) {
  const { t } = useT();
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="button"
      tabIndex={-1}
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div
        className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-lg border border-border bg-card p-4 shadow-lg"
        role="presentation"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium">
            {t('conversations.checklist.notRated', { n: String(total) })}
          </span>
          <button type="button" onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">
            {t('common.close')}
          </button>
        </div>
        {groups.map((g) => (
          <div key={g.stage} className="mb-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.09em] text-muted-foreground/80">
              {t(`conversations.stage.${g.stage}`)}
            </div>
            {g.items.map((i) => (
              <div key={String(i.key)} className="flex items-baseline justify-between gap-3 py-[3px] text-[13px]">
                <span className="min-w-0 text-muted-foreground">
                  {t(`conversations.checklist.item.${i.key}`)}
                </span>
                <span className="shrink-0 text-muted-foreground/70">{t('conversations.checklist.na')}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChecklistGrid({ raw, zoneId }: { raw: ConversationRawLlmResponse; zoneId?: number }) {
  const { t } = useT();
  // Пункты по точке (Настройки → Анализ речи): false — не показываем, true у выключенного по
  // умолчанию — оцениваем (неприменим только по ON_WHEN).
  const { data: zs } = useSpeechSettings(zoneId);
  const setting = (i: Item) => zs?.items?.[i.key as string];
  const naOf = (i: Item) => {
    const v = setting(i);
    if (v === false) return true;
    if (v === true && DEFAULT_OFF_ITEMS.has(i.key as string)) return ON_WHEN[i.key as string]?.(raw) === true;
    return i.naWhen?.(raw) === true;
  };
  const missing = (i: Item) => !i.asChoice && itemValue(raw, i.key) === undefined;
  const skip = (i: Item) => naOf(i) || missing(i);
  const naGroups: { stage: string; items: Item[] }[] = [];
  const cards = STAGES.map((st) => {
    // Этап, которого не было вовсе (нет приветствия, нет апселла, про
    // программу не спросили): карточка сворачивается, все пункты внутри N/A.
    const absent = st.optional != null && itemValue(raw, st.optional) === false;
    const na: Item[] = [];
    const live = absent ? st.items : st.items.filter((i) => (skip(i) ? (na.push(i), false) : true));
    const liveInfo = absent ? st.info : st.info.filter((i) => (skip(i) ? (na.push(i), false) : true));
    if (na.length) naGroups.push({ stage: st.stage, items: na });
    return { st, live, liveInfo, absent, hasItems: live.length + liveInfo.length > 0 };
  });
  const naCount = naGroups.reduce((n, g) => n + g.items.length, 0);
  const [showNa, setShowNa] = useState(false);

  return (
    <div>
      {(raw.problems?.length ?? 0) > 0 && <ProblemList problems={raw.problems!} />}
      {/* Газетные колонки: карточки идут подряд и заполняют сначала левую
          колонку, потом правую. Карточка занимает столько, сколько занимает. */}
      <div className="columns-1 gap-2 lg:columns-2">
        {cards.filter((c) => c.hasItems).map((c) => (
          <StageCard key={c.st.stage} st={c.st} live={c.live} liveInfo={c.liveInfo} raw={raw} absent={c.absent} />
        ))}
        {naCount > 0 && (
          // Последним пунктом в потоке колонок — то есть внизу правой колонки,
          // как владелец и просил, а не отдельной строкой под сеткой.
          <div className="break-inside-avoid pt-0.5">
            <button
              type="button"
              onClick={() => setShowNa(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-[12px] text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            >
              {t('conversations.checklist.hiddenOpen', { n: String(naCount) })}
            </button>
          </div>
        )}
      </div>
      {showNa && <NotRatedDialog groups={naGroups} onClose={() => setShowNa(false)} />}
    </div>
  );
}

function DetailBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

/** Сколько пунктов чек-листа сошлось хорошо и сколько плохо.
 *
 * Балл владелец из строки убрал: одна цифра «4.3» ничего не объясняет и спорит
 * с чек-листом, из которого посчитана. Два счётчика объясняют: что-то хорошее
 * случилось столько-то раз, что-то плохое столько-то. Пункты, которых просто не
 * было, ни в один счётчик не идут — это не провал, а пропуск.
 */
function checklistCounts(raw: ConversationRawLlmResponse): { good: number; bad: number } {
  // Этапа «Апселл» не было при поводе — красный крест (владелец, 14.09). Бэкенд
  // считает его сам с marks.v=2; к разговорам, разобранным раньше, добавляем здесь.
  const upsellMissing = raw.upsell_offered === false && raw.upsell_no_chance !== true
    && raw.upsell_filter_instead_americano !== true;
  // Считает бэкенд и кладёт в raw.marks — здесь только запасной путь для
  // разговоров, разобранных до перехода на этапы.
  if (raw.marks) {
    return (raw.marks.v ?? 0) >= 2 ? raw.marks
      : { good: raw.marks.good, bad: raw.marks.bad + (upsellMissing ? 1 : 0) };
  }
  let good = 0;
  let bad = upsellMissing ? 1 : 0;
  for (const { items } of STAGES) {
    for (const item of items) {
      if (item.naWhen?.(raw)) continue;
      if (itemValue(raw, item.key) !== true) continue;
      if (item.kind === 'bad') bad += 1;
      else if (item.kind === 'good') good += 1;
    }
  }
  return { good, bad };
}

/** Цвет проблемы по важности: жёлтый → оранжевый → красный (владелец, 15.09). */
const SEVERITY_CLASS: Record<ProblemSeverity, string> = {
  low: 'bg-yellow-400/20 text-yellow-800 dark:text-yellow-300',
  medium: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
  high: 'bg-red-500/15 text-red-700 dark:text-red-400',
};

/** Ярлык «Проблема» в строке: цвет по самой серьёзной, подпись — где она.
 *  Разговоры, разобранные до классов проблем, остаются красными, как были. */
function ProblemChip({ raw }: { raw: ConversationRawLlmResponse | null | undefined }) {
  const { t } = useT();
  const problems = raw?.problems ?? [];
  const sev: ProblemSeverity = raw?.problem_severity ?? (problems.length ? 'low' : 'high');
  const kinds = [...new Set(problems.filter((p) => p.severity === sev).map((p) => p.kind))];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium ${SEVERITY_CLASS[sev]}`}>
      <AlertTriangle className="size-3" />
      {t('conversations.row.problem')}
      {kinds.length > 0 && `: ${kinds.map((k) => t(`conversations.problem.kind.${k}`)).join(', ')}`}
    </span>
  );
}

/** Список проблем разговора над чек-листом. */
function ProblemList({ problems }: { problems: ConversationProblem[] }) {
  const { t } = useT();
  const order: Record<ProblemSeverity, number> = { high: 0, medium: 1, low: 2 };
  return (
    <div className="mb-2 rounded-md border px-3 py-2">
      <div className="mb-1 text-xs font-medium text-muted-foreground">{t('conversations.detail.problems')}</div>
      <ul className="space-y-1 text-[13px]">
        {[...problems].sort((a, b) => order[a.severity] - order[b.severity]).map((p, i) => (
          <li key={i} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${SEVERITY_CLASS[p.severity]}`}>
              {t(`conversations.problem.kind.${p.kind}`)} · {t(`conversations.problem.severity.${p.severity}`)}
            </span>
            <span className="min-w-0 break-words text-foreground/80">{p.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScoreChips({ raw }: { raw: ConversationRawLlmResponse | null | undefined }) {
  if (!raw) return <span className="w-16 shrink-0" />;
  const { good, bad } = checklistCounts(raw);
  // Оба числа видны всегда; ноль красится серым — «ни хорошего, ни плохого»
  // означает общение по стандарту, а не отсутствие данных.
  return (
    <span className="flex w-16 shrink-0 items-center justify-center gap-2 tabular-nums">
      <span className={`inline-flex items-center gap-0.5 ${good > 0 ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
        <Check className="size-3.5" />
        {good}
      </span>
      <span className={`inline-flex items-center gap-0.5 ${bad > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
        <X className="size-3.5" />
        {bad}
      </span>
    </span>
  );
}

/**
 * One conversation as a collapsible row. Summary line stays scannable (time /
 * duration / score / problem badge / clip count); the expanded body shows
 * recommendations, the staff-only 22-criteria checklist (only when
 * raw_llm_response is present — stripped for non-staff), the reconstructed
 * dialogue, audio clips, and the transcript.
 */
/** Разбор текста на слова с их позициями — по нему и ведём подсветку. */
const WORD_RE = /[0-9A-Za-zА-Яа-яЁё-]+/g;

/**
 * Основы слов запроса. Поиск на бэке ищет двумя способами сразу — вхождением
 * подстроки И по морфологии («бонусы» находят «бонус»), — поэтому подсветка
 * тоже не может быть только посимвольной: иначе найденный разговор показывался
 * бы вообще без выделения. Морфологию браузеру взять неоткуда, поэтому берём
 * то, что от неё видно глазами: начало слова. Четыре буквы — компромисс,
 * проверенный на наших запросах: «бону» ловит «бонус» и «бонусов», «паке» —
 * «пакетик», и при этом не хватает лишнего.
 */
function stems(q?: string): string[] {
  return (q || '')
    .toLowerCase()
    .split(/[^0-9a-zа-яё-]+/i)
    .filter((w) => w.length >= 2)
    .map((w) => w.slice(0, Math.max(4, w.length - 3)));
}

/** Слово похоже на запрошенное: целиком или началом. */
function wordHit(word: string, roots: string[]): boolean {
  const w = word.toLowerCase();
  return roots.some((r) => w.startsWith(r));
}

/**
 * Подсветка найденного. Порядок такой:
 *   1. точное вхождение запроса — выделяется ровно так, как набрано;
 *   2. слова, которые НАЗВАЛ БЭК (match.terms) — он искал, он и знает, что
 *      совпало: на опечатке («капуччино» нашло «капучино») угадать нечем;
 *   3. запасной путь — слова с той же основой, если бэк ничего не прислал
 *      (старый ответ без поля match).
 */
// Метки внутри реплики («‹неразборчиво› капучино без ‹неразборчиво›») — серым курсивом, без уголков
// (Алексей 03.11 #3570: «форматирование едет»).
function withMarks(text: string): React.ReactNode {
  const parts = text.split(/([‹<][^‹›<>]+[›>])/);
  if (parts.length === 1) return text;
  return parts.map((p, i) => (i % 2
    ? <span key={i} className="italic text-muted-foreground/60">{p.slice(1, -1).toLowerCase()}</span>
    : p));
}

function Hit({ text, q, terms }: { text: string; q?: string; terms?: string[] }) {
  const needle = (q || '').trim();
  if (!needle || !text) return <>{text ? withMarks(text) : text}</>;

  const hay = text.toLowerCase();
  const nl = needle.toLowerCase();
  const out: React.ReactNode[] = [];
  let from = 0;
  for (;;) {
    const at = hay.indexOf(nl, from);
    if (at < 0) break;
    if (at > from) out.push(text.slice(from, at));
    out.push(
      <mark key={`s${at}`} className="rounded-sm bg-amber-200/80 px-0.5 text-foreground dark:bg-amber-400/30">
        {text.slice(at, at + needle.length)}
      </mark>,
    );
    from = at + needle.length;
  }
  if (out.length) {
    out.push(text.slice(from));
    return <>{out}</>;
  }

  // Слова от бэка: сравниваем ЦЕЛИКОМ, без «начинается с» — он прислал ровно
  // те формы, что стоят в этом разговоре.
  const exact = new Set((terms || []).map((w) => w.toLowerCase()));
  const roots = exact.size ? [] : stems(needle);
  if (!exact.size && !roots.length) return <>{withMarks(text)}</>;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const m of text.matchAll(WORD_RE)) {
    const at = m.index ?? 0;
    const ok = exact.size ? exact.has(m[0].toLowerCase()) : wordHit(m[0], roots);
    if (!ok) continue;
    if (at > cursor) parts.push(text.slice(cursor, at));
    parts.push(
      <mark key={`w${at}`} className="rounded-sm bg-amber-200/80 px-0.5 text-foreground dark:bg-amber-400/30">
        {m[0]}
      </mark>,
    );
    cursor = at + m[0].length;
  }
  if (!parts.length) return <>{withMarks(text)}</>;
  parts.push(text.slice(cursor));
  return <>{parts}</>;
}

/** Первая реплика разговора, в которую попал запрос, — её и показываем в
 *  свёрнутом ряду вместо описания: искали по словам, значит слова и нужны.
 *  Номер реплики приходит от бэка (match.line); угадывание ниже осталось
 *  запасным путём — для старого ответа без поля match и для разговоров,
 *  разобранных до 10.09.2026, у которых реплик списком нет вовсе. */
function firstMatch(lines: DialogueLine[], q?: string, at?: number | null): string | null {
  if (at != null && lines[at]) return (lines[at].text || '').trim();
  const needle = (q || '').trim().toLowerCase();
  if (!needle) return null;
  const exact = lines.find((l) => (l.text || '').toLowerCase().includes(needle));
  if (exact) return (exact.text || '').trim();
  const roots = stems(needle);
  if (!roots.length) return null;
  const byStem = lines.find((l) =>
    [...(l.text || '').matchAll(WORD_RE)].some((m) => wordHit(m[0], roots)),
  );
  return byStem ? (byStem.text || '').trim() : null;
}

/** Крутилка вместо списка, пока грузится другой день: одна подпись «Загрузка…»
 *  без движения выглядела как зависшая страница. */
function ListLoader() {
  const { t } = useT();
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
      {t('common.loading')}
    </div>
  );
}

const ID_QUERY = /^\s*(?:id\s*[:=]|#)\s*\d+\s*$/i;
/** Особые запросы рисуем чипом, а не текстом в поле (Алексей 17.09, AI19). */
// Виды — заголовки баров сводки плюс «проблема»; тот же список у бэкенда
// строится из пресетов (summary.facet_table). Меняется заголовок бара — надо
// поправить и здесь, иначе чип перестанет рисоваться (Алексей 21.09, AI95).
// Третья часть — уточнение (#1404): «апселл: еда к напитку: хот-дог», «проблема: нет товара: фильтр-кофе».
const FACET_QUERY = /^\s*(проблема|признак|общение|апселл|программа лояльности)\s*:\s*([^:]+?)\s*(?::\s*(.+?))?\s*$/i;
const FACET_TONE: Record<string, string> = {
  // Цвет — под то, на что нажали выше: проблемы оранжевые, общение синее.
  проблема: 'border-orange-500/40 bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  общение: 'border-sky-500/40 bg-sky-50 text-sky-800 dark:bg-sky-900/20 dark:text-sky-300',
  апселл: 'border-emerald-500/40 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300',
  'программа лояльности': 'border-violet-500/40 bg-violet-50 text-violet-800 dark:bg-violet-900/20 dark:text-violet-300',
  признак: 'border-slate-500/40 bg-slate-50 text-slate-800 dark:bg-slate-900/20 dark:text-slate-300',
};

/** Булевы пункты чек-листа судьи для «признак: …» (AI254) — подписи те же, что на карточке. */
const FLAG_KEYS = ['rude', 'dry_minimal', 'friendly', 'active_communication', 'interesting_communication', 'positive_closing', 'asked_receipt', 'told_wait_time', 'greeting', 'asked_volume', 'asked_here_or_togo', 'offered_ceramic', 'repeated_order', 'warned_wait_before_pay', 'named_total', 'payment_taken', 'asked_loyalty', 'offered_loyalty_signup', 'upsell_offered', 'upsell_accepted', 'client_own_cup'];

function searchChip(query: string): { kind: string; sub?: string; value: string; tone?: string } | null {
  const id = query.match(/^\s*(?:id\s*[:=]|разговор\s*[:=]|#)\s*(\d+)\s*$/i);
  if (id) return { kind: 'разговор', value: id[1], tone: 'border-primary/30 bg-primary/5 text-primary' };
  const f = query.match(FACET_QUERY);
  if (f) {
    const kind = f[1].toLowerCase();
    return f[3] ? { kind, sub: f[2], value: f[3], tone: FACET_TONE[kind] } : { kind, value: f[2], tone: FACET_TONE[kind] };
  }
  return null;
}

/** Конструктор особого чипа в поле поиска (Алексей 09.10, #1404; переделан 12.10, AI253:
 *  «системные дропдауны и не оч понятно»): общий ChipStepPicker — что ищем → значение →
 *  уточнение. Собирает тот же запрос, что клик по сводке, — «апселл: еда + напиток:
 *  хот-дог», «проблема: нет товара: фильтр-кофе», «признак: явная невежливость»,
 *  «разговор: 21315». */
function ChipBuilder({ overview, onPick, title }: { overview: OverviewData | null; onPick: (query: string) => void; title: string }) {
  const { t } = useT();
  const bars = overview?.bars ?? [];
  const kinds: PickerKind[] = [
    ...bars.map((b) => { const key = b.title.trim().toLowerCase(); return { key, label: b.title, tone: FACET_TONE[key] }; }),
    { key: 'проблема', label: 'Проблема', tone: FACET_TONE['проблема'] },
    { key: 'признак', label: 'Признак', tone: FACET_TONE['признак'] },
    { key: 'разговор', label: 'Разговор №', tone: 'border-primary/30 bg-primary/5 text-primary' },
  ];
  const barOf = (kind: string) => bars.find((b) => b.title.trim().toLowerCase() === kind);
  const valuesFor = (kind: string): PickerValue[] => {
    const bar = barOf(kind);
    if (bar) {
      return [
        ...bar.values.filter((v) => v.count > 0).map((v) => ({ key: v.label, label: v.label, count: v.count })),
        ...[...new Set(bar.values.filter((v) => v.examples_title && v.examples.length).map((v) => v.examples_title!))].map((x) => ({ key: x, label: x, any: true })),
      ];
    }
    if (kind === 'проблема') return (overview?.problems.kinds ?? []).map((k) => ({ key: k.label, label: k.label, count: k.count }));
    if (kind === 'признак') return FLAG_KEYS.map((k) => { const label = t(`conversations.checklist.item.${k}`); return { key: label, label }; });
    return [];
  };
  const optionsFor = (kind: string, value: string): [string, number][] => {
    const bar = barOf(kind);
    if (bar) return mergeCounts(bar.values.filter((v) => v.label === value || v.examples_title === value).flatMap((v) => v.examples));
    if (kind === 'проблема') return mergeCounts((overview?.problems.kinds ?? []).filter((k) => k.label === value).flatMap((k) => k.subjects));
    return [];
  };
  const query = (kind: string, value: string, text: string) => (kind === 'разговор' ? `разговор: ${value}` : `${kind}: ${value}${text ? `: ${text}` : ''}`);
  return (
    <ChipStepPicker
      title={title}
      kinds={kinds}
      valuesFor={valuesFor}
      optionsFor={optionsFor}
      numericKinds={['разговор']}
      textPlaceholder={(kind) => (kind === 'признак' ? null : barOf(kind) ? 'или свой текст: позиция, слово из формулировки' : 'или свой текст: предмет, напр. фильтр-кофе')}
      preview={(kind, value, text) => searchChip(query(kind, value, text))}
      onPick={(kind, value, text) => onPick(query(kind, value, text))}
    />
  );
}

/** Сложить одинаковые формулировки из нескольких значений и отсортировать по частоте. */
function mergeCounts(pairs: [string, number][]): [string, number][] {
  const m = new Map<string, number>();
  for (const [t, n] of pairs) m.set(t, (m.get(t) ?? 0) + n);
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
}

/** Кнопка «скопировать ссылку на разговор» (Алексей 18.09, AI72). */
function ConversationLink({ id }: { id: number }) {
  const [copied, setCopied] = useState(false);
  const href = () => {
    const u = new URL(window.location.href);
    u.searchParams.set('tab', 'conversations');
    u.searchParams.set('conv', String(id));
    return u.toString();
  };
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span>Разговор {id}</span>
      <button
        type="button"
        onClick={async (e) => {
          e.preventDefault();
          try {
            await navigator.clipboard.writeText(href());
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            // Буфер закрыт политикой браузера — ссылку видно в адресной строке
            // после того, как разговор открыт по conv.
          }
        }}
        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 hover:bg-muted"
      >
        {copied ? <Check className="size-3" /> : <Link2 className="size-3" />}
        {copied ? 'скопировано' : 'ссылка'}
      </button>
    </div>
  );
}

function ConversationRow({ c, q, openByDefault = false }: { c: Conversation; q?: string; openByDefault?: boolean }) {
  const { t } = useT();
  const qs = c.quality_score;
  const raw = qs?.raw_llm_response;
  const time = new Date(c.started_at).toLocaleString('ru-RU', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const durationMin = c.duration_seconds != null ? (c.duration_seconds / 60).toFixed(1) : '—';
  const clips = c.audio_clips ?? [];
  const segments = c.segments ?? [];
  const dialogueLines = raw?.dialogue_lines ?? [];
  // Когда искали — в ряду показываем НАЙДЕННУЮ реплику, а не описание разговора:
  // иначе непонятно, за что он попал в список.
  const matched = firstMatch(dialogueLines, q, c.match?.line);
  const terms = c.match?.terms;
  // Раскрыт ли ряд: свёрнутый не тянет звук (см. preload в useClipPlayer).
  const [open, setOpen] = useState(openByDefault);

  return (
    // open — управляемый: разговор, открытый из сводки по номеру, должен
    // раскрыться сразу, а не только запомниться в состоянии (AC12).
    <details className="group" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted/30 group-open:items-start max-sm:group-open:gap-y-1.5">
        <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        <span className="shrink-0 whitespace-nowrap font-mono text-xs">{time}</span>
        <span className="shrink-0 whitespace-nowrap tabular-nums text-muted-foreground">{durationMin}m</span>
        <ScoreChips raw={raw} />
        {c.status === 'PARTIAL' && (
          // Обслуживание, записанное кусками: диалог есть, чек-листа нет — и
          // это должно быть видно сразу, чтобы пустой чек-лист не читался как
          // «кассир ничего не сделал».
          <span className="shrink-0 whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {t('conversations.status.PARTIAL')}
          </span>
        )}
        {/* В свёрнутом ряду описание обрезано в одну строку, в раскрытом —
            переносится по словам и тянет высоту: дублировать тот же текст
            внутри карточки незачем.
            На телефоне в раскрытом ряду описание уходит ПОД время и галочки,
            на всю ширину (basis-full при flex-wrap у строки). Иначе ему
            оставалось ~80 px после времени, длительности и счётчиков, и текст
            вставал столбиком по слову на строку (Эдем, 14.09). В свёрнутом
            ряду flex-1 даёт нулевую основу, и описание остаётся в строке. */}
        <span className="min-w-0 flex-1 truncate text-muted-foreground group-open:whitespace-normal group-open:break-words max-sm:group-open:basis-full">
          {matched ? <Hit text={matched} q={q} terms={terms} /> : qs?.summary}
        </span>
        {/* Бейдж проблемы — справа, после описания: слева он сдвигал начало текста
            у одних рядов и не сдвигал у других (владелец, 15.09). */}
        {qs?.has_problem && <ProblemChip raw={raw} />}
      </summary>
      <div className="space-y-4 px-4 pb-5 pt-1 text-sm">
        {/* Ссылка на этот самый разговор (Алексей 18.09, AI72). Берём текущий
            адрес и подставляем conv: в нём уже стоят филиал и период, поэтому
            получателю откроется ровно то же, что видит отправитель. */}
        <ConversationLink id={c.id} />

        {/* Широкий экран: диалог слева, оценки справа — читаешь реплику и тут
            же видишь, что по ней засчитано. Узкий: всё в столбик, диалог
            первым, потому что он главное. Без заголовка «Чек-лист»: он ничего
            не добавлял к самим строкам, а место занимал. */}
        {dialogueLines.length > 0 && clips.length > 0 ? (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] xl:gap-8">
            {/* Плеер и диалог — одна белая карточка, а не белый плеер поверх
                серой подложки (так две подложки спорили). От серых карточек
                оценок её отделяет просто воздух. */}
            <div className="min-w-0">
              <DialogueBoard
                q={q}
                terms={terms}
                clips={clips}
                lines={dialogueLines}
                problems={raw?.problems ?? []}
                transcript={raw?.transcript_lines ?? []}
                offset={raw?.player_offset_sec ?? 0}
                endSec={raw?.player_end_sec}
                rawFallback={c.full_transcription}
                fullUrl={c.audio_url_full}
                expanded={open}
                convId={c.id}
                markup={c.markup}
              />
            </div>
            {raw && <div className="min-w-0"><ChecklistGrid raw={raw} zoneId={c.zone} /></div>}
          </div>
        ) : (
          <>
            {raw && <ChecklistGrid raw={raw} zoneId={c.zone} />}
            {raw?.reconstructed_dialogue && (
              <DetailBlock title={t('conversations.detail.dialogue')}>
                <p className="whitespace-pre-wrap break-words text-muted-foreground">{raw.reconstructed_dialogue}</p>
              </DetailBlock>
            )}
            {clips.length > 0 && (
              <DetailBlock title={t('conversations.table.recording')}>
                <AudioClips clips={clips} />
              </DetailBlock>
            )}
            <DetailBlock title={t('conversations.detail.transcript')}>
              {segments.length > 0 ? (
                // Staff-only per-segment transcripts (serializer omits `segments` for non-staff).
                <ol className="space-y-0.5">
                  {segments.map((s) => (
                    <li key={s.sequence_number} className="flex gap-2 break-words">
                      <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">#{s.sequence_number}</span>
                      <span className="whitespace-pre-wrap text-muted-foreground">
                        {s.transcription_text || t('conversations.internal.empty')}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="whitespace-pre-wrap break-words text-muted-foreground">
                  {c.full_transcription || t('conversations.internal.empty')}
                </p>
              )}
            </DetailBlock>
          </>
        )}
      </div>
    </details>
  );
}

// Блок «Сырые данные (только Linza)» удалён 16.09 (Алексей): на вкладке Речь его
// заменил бесконечный скролл по разговорам, а клипы со всеми полями — статусом
// словами, точным интервалом записи, репликами с таймингами — живут в админке
// /admin/data на вкладке аудио. Две ленты одного и того же звука на одной странице
// были лишним шумом.

export interface AnalyticsConversationsTabProps {
  zoneId: string;
  from: string;
  to: string;
  /** Reserved for future bucket-by-week support; current backend only buckets by hour/day. */
  granularity?: 'hour' | 'day' | 'week';
}

export default function AnalyticsConversationsTab({ zoneId, from, to }: AnalyticsConversationsTabProps) {
  const { t } = useT();
  const { currentZone } = useCurrentZone();

  // No-mic branches see a value-prop instead of empty charts. Skip the
  // BE calls entirely so we don't generate noise on the conversations
  // summary endpoint for branches that will always return zero. Mic
  // presence comes from Zone.has_mic (an enabled Microphone equipment
  // record) — see equipment_discovery + ZoneSerializer.get_has_mic.
  const hasMic = currentZone?.has_mic ?? false;

  // Показывать ли обслуживания, записанные кусками. По умолчанию нет: их бывает
  // больше, чем целых, и список из них не читается.
  const [showPartial, setShowPartial] = useState(false);

  // Поиск по тексту разговора. Два состояния намеренно: `draft` — то, что набрано
  // в поле, `search` — то, что отправлено кнопкой «Найти». Искать на каждое
  // нажатие клавиши нельзя: запрос идёт в базу последовательным просмотром по
  // jsonb, и поиск «хот-дог» стоил бы семи запросов вместо одного.
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  // Список: к нему прокручиваемся, открыв разговор из сводки.
  const listRef = useRef<HTMLDivElement | null>(null);

  // Диплинк на разговор (Алексей 18.09, AI72): `?conv=21315` — то же самое, что
  // вбить «id: 21315» руками, но ссылкой, которую можно переслать. Отдельной
  // ветки в списке не нужно: этот вид запроса уже сужает выдачу до одного
  // разговора и раскрывает ряд (openByDefault ниже).
  const [searchParams, setSearchParams] = useSearchParams();
  const convParam = searchParams.get('conv');
  const seededConv = useRef<string | null>(null);
  useEffect(() => {
    if (!convParam || seededConv.current === convParam) return;
    seededConv.current = convParam;
    setDraft(`#${convParam}`);
    setSearch(`#${convParam}`);
  }, [convParam]);
  // Сбросили поиск — убираем и параметр, иначе адрес обещает разговор, которого
  // на экране уже нет, и обновление страницы возвращало бы его обратно.
  const dropConvParam = useCallback(() => {
    seededConv.current = null;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('conv');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // The summary endpoint (KPIs, score/count timeseries, criteria averages) is no
  // longer fetched: Alexey asked 09.09 to leave only the conversations table on
  // this tab, so nothing consumed it and the call was pure load per page view.
  //
  // Список не постраничный (Алексей 16.09): кончились разговоры за период —
  // листаешь дальше и видишь прежние, отделённые заголовком. Поэтому здесь не
  // SWR на страницу, а накопление: страницы приклеиваются к уже показанному, а
  // сбрасывается всё только при смене выборки.
  const listScope = `${zoneId}|${from}|${to}|${showPartial}|${search}`;
  const [items, setItems] = useState<Conversation[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  // «period» — внутри выбранных дат, «earlier» — то, что было до них.
  const [phase, setPhase] = useState<'period' | 'earlier'>('period');
  const [firstEarlierId, setFirstEarlierId] = useState<number | null>(null);
  const [done, setDone] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const pageRef = useRef(1);
  const scopeRef = useRef(listScope);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scopeRef.current = listScope;
    pageRef.current = 1;
    setItems([]);
    setTotalCount(0);
    setPhase('period');
    setFirstEarlierId(null);
    setDone(false);
    setListError(false);
  }, [listScope]);

  const loadNext = useCallback(async () => {
    if (!zoneId || !hasMic || done || listLoading) return;
    const scope = scopeRef.current;
    setListLoading(true);
    setListError(false);
    try {
      const inPeriod = phase === 'period';
      // «Ранее» — всё до начала выбранного периода; берём с запасом назад, чтобы
      // не гадать, когда филиал начал записывать.
      const earlierTo = new Date(Date.parse(`${from}T00:00:00Z`) - 86400000).toISOString().slice(0, 10);
      const resp = await getConversations(
        Number(zoneId),
        inPeriod ? from : '2000-01-01',
        inPeriod ? to : earlierTo,
        pageRef.current,
        { withPartial: showPartial, q: search },
      );
      // Ответ чужой выборки (успели сменить день) не показываем вовсе.
      if (scopeRef.current !== scope) return;
      if (inPeriod) setTotalCount(resp.count ?? 0);
      setItems((prev) => {
        const known = new Set(prev.map((c) => c.id));
        const fresh = (resp.results ?? []).filter((c) => !known.has(c.id));
        if (!inPeriod && fresh.length && firstEarlierId === null) setFirstEarlierId(fresh[0].id);
        return [...prev, ...fresh];
      });
      if (pageRef.current >= (resp.num_pages ?? 1)) {
        if (inPeriod) {
          // Период кончился — продолжаем тем, что было раньше.
          setPhase('earlier');
          pageRef.current = 1;
        } else {
          setDone(true);
        }
      } else {
        pageRef.current += 1;
      }
    } catch {
      // Останавливаем ленту: без этого наблюдатель прокрутки будет повторять
      // один и тот же падающий запрос бесконечно.
      if (scopeRef.current === scope) { setListError(true); setDone(true); }
    } finally {
      if (scopeRef.current === scope) setListLoading(false);
    }
  }, [zoneId, hasMic, done, listLoading, phase, from, to, showPartial, search, firstEarlierId]);

  // Лента грузится мимо SWR — общий «перезапросить» (кнопка «Обновить» и возврат сервера после деплоя)
  // её не трогал: «не получилось подключиться» висело до перезагрузки страницы (Алексей 27.10 #2972).
  // Сброс ошибки и done снова включает наблюдатель прокрутки, и он догружает с той же страницы.
  const retryList = useCallback(() => { setListError(false); setDone(false); }, []);
  const health = useServerHealth();
  useEffect(() => {
    if (health.kind === 'back' && listError) retryList();
  }, [health.kind, listError, retryList]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || done || !hasMic) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) void loadNext();
    }, { rootMargin: '600px' });
    io.observe(node);
    return () => io.disconnect();
  }, [loadNext, done, hasMic]);

  if (!hasMic) {
    return <NoMicValueProp />;
  }

  // Данные ЧУЖОЙ выборки не показываем вовсе (при переходе «сегодня → вчера»
  // вчерашний список грузился под старыми рядами и сайт выглядел зависшим —
  // владелец, 15.09): смена выборки очищает накопленное в эффекте выше.
  const conversations: Conversation[] = items;
  const firstLoad = listLoading && conversations.length === 0;
  // Сводка — конструктору чипов: бары, значения, формулировки, виды проблем (#1404).
  const [overview, setOverview] = useState<OverviewData | null>(null);

  return (
    <div className="space-y-4">
      {/* Сводка за период над списком (AB8): бары по параметрам разговора и проблемы. */}
      {zoneId && (
        <ConversationsOverview
          zoneId={zoneId}
          from={from}
          to={to}
          // «К разговору» из примера: особый запрос по номеру (AC12). Бэкенд по
          // нему ищет один разговор и не применяет период — пример может быть
          // из другого дня, чем выбранный.
          onOpen={(id) => {
            const query = `id:${id}`;
            setDraft(query);
            setSearch(query);
            listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          // Клик по доле/легенде/плашке проблемы — особый запрос чипом (AI19).
          onFacet={(query) => {
            setDraft(query);
            setSearch(query);
            listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          onData={setOverview}
        />
      )}
      {/* «Явные недопродажи» переехали на вкладку «Недопродажи», секция «По голосу» (Алексей 03.11 #3567) */}
      {/* Paginated table of analyzed conversations */}
      <Card ref={listRef}>
        <div className="px-4 py-3 border-b flex items-baseline justify-between gap-2">
          <div className="text-sm font-medium">
            {t('conversations.table.title')}
            <span className="ml-2 text-xs font-normal text-muted-foreground">({totalCount})</span>
          </div>
          <div className="text-xs text-muted-foreground">{t('conversations.table.subtitle')}</div>
        </div>
        {/* Поиск по тексту разговора за УЖЕ выбранный на странице период:
            отдельного выбора дат здесь намеренно нет, иначе на странице было бы
            два разных периода. Форма, а не просто кнопка, — чтобы Enter в поле
            работал как «Найти». */}
        {/* Поиск: особый запрос («разговор: 21315», «проблема: нет товара»)
            показывается чипом в поле, обычный текст — как раньше (AI19). */}
        <SearchChipInput
          className="border-b px-4 py-2.5"
          draft={searchChip(draft) ? '' : draft}
          onDraftChange={setDraft}
          onSubmit={() => setSearch(draft.trim())}
          onReset={() => { setDraft(''); setSearch(''); dropConvParam(); }}
          chip={searchChip(draft)}
          onClearChip={() => { setDraft(''); setSearch(''); dropConvParam(); }}
          placeholder={t('conversations.search.placeholder')}
          submitLabel={t('conversations.search.button')}
          resetLabel={t('conversations.search.reset')}
          active={!!search || !!draft}
          extra={<ChipBuilder overview={overview} title={t('conversations.search.addChip')} onPick={(query) => { setDraft(query); setSearch(query); }} />}
        />
        {search && !listLoading && (
          <div className="border-b px-4 py-1.5 text-xs text-muted-foreground">
            {t('conversations.search.found', { count: String(totalCount), q: search })}
          </div>
        )}
        <div className="divide-y">
          {firstLoad && <ListLoader />}
          {listError && conversations.length === 0 && (
            <ServerStatusNotice variant="card" error={listError} onRetry={retryList} />
          )}
          {!firstLoad && !listError && conversations.length === 0 && (
            search
              ? <div className="px-4 py-6 text-center text-muted-foreground">{t('conversations.search.empty')}</div>
              : <NoDataForPeriod section="speech" what="разговоров" suggestion={{ label: 'Посмотреть за вчера', from: isoDay(-1), to: isoDay(-1) }} />
          )}
          {conversations.map((c) => (
            <div key={c.id}>
              {c.id === firstEarlierId && (
                // Граница периода: дальше идут разговоры, которые в выбранные
                // даты не попадают. Без неё лента врала бы про период.
                // Черта и сверху, и снизу, и с большим отступом: у плиток своя
                // нижняя граница, и одиночная линия сливалась с ней (Алексей 16.09).
                <div className="my-3 border-y bg-muted/30 px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="whitespace-nowrap text-xs uppercase tracking-wider text-muted-foreground">
                      Разговоры ранее
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                </div>
              )}
              {/* Разговор, открытый из сводки по номеру, разворачиваем сразу:
                  человек шёл смотреть именно его. */}
              {/* При чипе слова запроса не подсвечиваем: «без апселла» красил «без» в
                  «безлактозном» (Алексей 09.10, #1406). */}
              <ConversationRow c={c} q={searchChip(search) ? undefined : search} openByDefault={ID_QUERY.test(search)} />
            </div>
          ))}
        </div>
        {!done && <div ref={sentinelRef} className="h-4" />}
        {listLoading && conversations.length > 0 && (
          <div className="flex items-center justify-center gap-2 border-t px-4 py-3 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            {phase === 'earlier' ? 'Загружаем разговоры ранее…' : 'Загружаем…'}
          </div>
        )}
        {done && conversations.length > 0 && (listError ? (
          <ServerStatusNotice variant="card" className="border-t" onRetry={retryList} />
        ) : (
          <div className="border-t px-4 py-2 text-center text-xs text-muted-foreground">
            Это все разговоры филиала
          </div>
        ))}
        {/* Частично записанные — надстройка над списком, и выглядеть она должна
            соответственно: неприметная ссылка ВНИЗУ, а не чекбокс в шапке.
            Раньше её видели только сотрудники Linza; Эдем 15.09: клиенту во
            вкладке «Речь» видно всё, что нам, кроме «Сырых данных». */}
        <div className="border-t px-4 py-1.5 text-right">
          <button
            type="button"
            onClick={() => setShowPartial(!showPartial)}
            className="text-[11px] text-muted-foreground/50 underline-offset-2 hover:text-muted-foreground hover:underline"
          >
            {t(showPartial ? 'conversations.table.hidePartial' : 'conversations.table.showPartial')}
          </button>
        </div>
      </Card>

      {/* Блок «Сырые данные (только Linza)» убран отсюда (Алексей 16.09): его
          заменил бесконечный скролл по разговорам, а сами клипы со всеми полями
          живут в админке — /admin/data, вкладка аудио. Держать две ленты на одной
          странице значило показывать один и тот же звук дважды. */}
    </div>
  );
}
